import Logger from './Logger';
import hark from 'hark';

import appVerInfo from './version';

import { getSignalingUrl } from './urlFactory';
import { 
	SocketTimeoutError, 
	setCookies, 
	getCookies, 
	getErrMesage,
	clone 
} from './utils';
import * as requestActions from './actions/requestActions';
import * as meActions from './actions/meActions';
import * as intlActions from './actions/intlActions';
import * as roomActions from './actions/roomActions';
import * as peerActions from './actions/peerActions';
import * as peerVolumeActions from './actions/peerVolumeActions';
import * as settingsActions from './actions/settingsActions';
import * as chatActions from './actions/chatActions';
import * as fileActions from './actions/fileActions';
import * as lobbyPeerActions from './actions/lobbyPeerActions';
import * as consumerActions from './actions/consumerActions';
import * as producerActions from './actions/producerActions';
import * as notificationActions from './actions/notificationActions';
import * as transportActions from './actions/transportActions';
import * as subtitleActions from './actions/subtitleActions';

import { AwaitQueue }  from 'awaitqueue';

import * as locales from './translations/locales';
import { createIntl } from 'react-intl';

import Spotlights from './Spotlights';

import { 
	screenConsumerSelector, 
	meIsCohostSelector, 
	meIsModeratorSelector 
} from './components/Selectors';

import watermark from './watermark';

import VirtualReality from './VirtualReality';
//import JitsiVirtualReality from './JitsiVirtualReality';

let createTorrent;
let WebTorrent;
let saveAs;
let mediasoupClient;
let io;
let ScreenShare;

let requestTimeout,
	transportOptions;

if (process.env.NODE_ENV !== 'test')
{
	({
		requestTimeout,
		transportOptions
	} = window.config);
}

const logger = new Logger('RoomClient');

const ROOM_OPTIONS =
{
	requestTimeout   : requestTimeout,
	transportOptions : transportOptions
};

const VIDEO_CONSTRAINS =
{
	'low' :
	{
		width       : { ideal: 320 },
		aspectRatio : 1.778
	},
	'medium' :
	{
		width       : { ideal: 640 },
		aspectRatio : 1.778
	},
	'high' :
	{
		width       : { ideal: 1280 },
		aspectRatio : 1.778
	},
	'veryhigh' :
	{
		width       : { ideal: 1920,max:1920 },
		aspectRatio : 1.778
	},
	'ultra' :
	{
		width       : { ideal: 3840 },
		aspectRatio : 1.778
	}
};

const PC_PROPRIETARY_CONSTRAINTS =
{
	optional : [ { googDscp: true } ]
};

const VIDEO_SIMULCAST_ENCODINGS =
[
	{ scaleResolutionDownBy: 4 },
	{ scaleResolutionDownBy: 2 },
	{ scaleResolutionDownBy: 1 }
];

// Used for VP9 webcam video.
const VIDEO_KSVC_ENCODINGS =
[
	{ scalabilityMode: 'S3T3_KEY' }
];

// Used for VP9 desktop sharing.
const VIDEO_SVC_ENCODINGS =
[
	{ scalabilityMode: 'S3T3', dtx: true }
];

const QLevelMapSpatialLayer = new Map().set(10, 2).set(7, 1).set(3, 0);

let store;

let intl;

export default class RoomClient
{
	/**
	 * @param  {Object} data
	 * @param  {Object} data.store - The Redux store.
	 * @param  {Object} data.intl - react-intl object
	 */
	static init(data)
	{
		store = data.store;
		intl = data.intl;
	}

	constructor(
		{
			peerId,
			accessCode,
			device,
			produce,
			forceTcp,
			displayName,
			muted,
			basePath
		} = {})
	{
		if (!peerId)
			throw new Error('Missing peerId');
		else if (!device)
			throw new Error('Missing device');

		logger.debug(
			'constructor() [peerId: "%s", device: "%s", produce: "%s", forceTcp: "%s", displayName ""]',
			peerId, device.flag, produce, forceTcp, displayName);

		this._signalingUrl = null;

		this._pushServerUrl = `wss://${window.location.hostname}:9443/`;

		this._pushClient = null;

		// Closed flag.
		this._closed = false;
		// Whether we should produce.
		this._produce = produce;
		// Whether we force TCP
		this._forceTcp = forceTcp;
		// URL basepath
		this._basePath = basePath;
		// Use displayName
		if (displayName) {
			store.dispatch(settingsActions.setDisplayName(displayName));
		}

		this._tracker = 'wss://tracker.lab.vvc.niif.hu:443';
		// Torrent support
		this._torrentSupport = null;
		// Whether simulcast should be used.
		this._useSimulcast = false;
		if ('simulcast' in window.config) {
			this._useSimulcast = window.config.simulcast;
		}

		// Whether simulcast should be used for sharing
		this._useSharingSimulcast = false;

		if ('simulcastSharing' in window.config) {
			this._useSharingSimulcast = window.config.simulcastSharing;
		}

		this._muted = muted;
		// This device
		this._device = device;
		this._oldPeerId = ''; // 旧的 peerId
		// My peer name.
		this._peerId = peerId;
		// Access code
		this._accessCode = accessCode;
		// Alert sound
		this._soundAlert = new Audio(`${window.app_base_path}/sounds/notify.mp3`);
		// Socket.io peer connection
		this._signalingSocket = null;

		// The room ID
		this._roomId = null;
		this._roomName = null;
		// mediasoup-client Device instance.
		// @type {mediasoupClient.Device}
		this._mediasoupDevice = null;
		// Put the browser info into state
		store.dispatch(meActions.setBrowser(device));
		// Our WebTorrent client
		this._webTorrent = null;
		// Transport for sending.
		this._sendTransport = null;
		// Transport for receiving.
		this._recvTransport = null;
		// Local mic mediasoup Producer.
		this._micProducer = null;
		// Local mic hark
		this._hark = null;
		// Local MediaStream for hark
		this._harkStream = null;
		// Local webcam mediasoup Producer.
		this._webcamProducer = null;

		this._webcamProducerCreated = false;

		this._isFirstRunSetMaxQLevel = true;  // 标注是否第一次执行
		this._recvMaxQLevel = -1;

		// Extra videos being produced
		this._extraVideoProducers = new Map();
		// Map of webcam MediaDeviceInfos indexed by deviceId.
		// @type {Map<String, MediaDeviceInfos>}
		this._webcams = {};
		this._audioDevices = {};
		this._audioOutputDevices = {};

		// mediasoup Consumers.
		// @type {Map<String, mediasoupClient.Consumer>}
		this._consumers = new Map();

		this._screenSharing = null;
		this._screenSharingProducer = null;

		this._applySharePeerId = null;

		// Joining queue
		this._queue = new AwaitQueue();
		this._heartbeats = null;  // 心跳

		this._mode = store.getState().room.mode;
		this._viewsLayout = window.viewsLayout; // 加载布局模板的配置
		// this._viewsLayout = null; // 加载布局模板的配置
		this._spotlights = new Spotlights(store, this);

		this._spotlightPeerIds = [];
		this._fullScreenConsumerId = null;

		this._startKeyListener();

		this._startDevicesListener();

		// Receive transport restart ICE object
		this._recvRestartIce = { timer: null, restarting: false };

		// Send transport restart ICE object
		this._sendRestartIce = { timer: null, restarting: false };

		this._startSpeakingTime = null;
		this._talkingTimes = 0;  // 第几次检测到说话

		//this._virtualReality = new JitsiVirtualReality();  // 虚拟背景工具类
		this._virtualReality = new VirtualReality();  // 虚拟背景工具类

		this._clearSubtitleTimer = null;

		this._watermarkTimer = null;

		this._pollCount = 0;

		this._subtitleTipCount = 0;  // 实时字幕提示次数累计

		this._iceParameters = null;
	}
	get roomId() {
		return this._roomId;
	}
	set roomId(value) {
		this._roomId = value;
	}

	get roomName() {
		return this._roomName;
	}
	set roomName(name) {
		this._roomName = name;
	}

	get peerId() {
		return this._peerId;
	}
	set peerId(value) {
		this._peerId = value;
	}

	get oldPeerId() {
		return this._oldPeerId;
	}
	set oldPeerId(value) {
		this._oldPeerId = value;
	}

	get mode() {
		return this._mode;
	}
	get viewsLayout() {
		return this._viewsLayout;
	}

	get virtualReality() {
		return this._virtualReality;
	}

	/**
	 * 初始化layoutConfig
	 * @param {*} layoutConfig
	 * @param {*} mode
	 * @param {*} force
	 */
	async initLayoutConfig(layoutConfig, mode, force = false) {

		await this._spotlights.initLayoutConfig(layoutConfig, mode, force)
	}

	close()
	{
		if (this._closed) {
			return;
		}
		this._closed = true;
		logger.debug('close()');

		this._signalingSocket.close();

		if (this._screenSharingProducer) {

			this._screenSharingProducer.close();
			store.dispatch(producerActions.removeProducer(this._screenSharingProducer.id));
			store.dispatch(roomActions.setMyScreenSharing(false));
			this._screenSharingProducer = null;
		}
		if (this._webcamProducer) {

			this._webcamProducer.close();
			store.dispatch(producerActions.removeProducer(this._webcamProducer.id));
			this._webcamProducer = null;
		}

		if (this._micProducer) {

			this._micProducer.close();
			store.dispatch(producerActions.removeProducer(this._micProducer.id));
			this._micProducer = null;
		}

		// Close mediasoup Transports.
		if (this._sendTransport) {

			this._sendTransport.close();
			this._sendTransport = null;
		}
		if (this._recvTransport) {

			this._recvTransport.close();
			this._recvTransport = null;
		}

		this._spotlights.clearSpotlights();

		store.dispatch(peerActions.clearPeers());
		store.dispatch(consumerActions.clearConsumers());
		store.dispatch(roomActions.clearSpotlights());

		store.dispatch(roomActions.setRoomState('closed'));

		if (this._queue) {

			this._queue.close();
		}
		this._queue = null;

		if (this._heartbeats) {

			clearInterval(this._heartbeats); // 关闭心跳
			logger.debug('heartbeats: close()');
		}

		if (this._clearSubtitleTimer) {

			clearTimeout(this._clearSubtitleTimer);
			logger.debug('clearSubtitleTimer: close()');
		}

		if (this._watermarkTimer) {

			clearInterval(this._watermarkTimer);
			logger.debug('watermarkTimer: close()');
		}

		// 通知第三方应用，用户离会
		if (window.parent) {

			const _iframe = window.parent.document.getElementsByName(store.getState().room.windowOpenName);
			if (_iframe.length > 0 && _iframe[0].tagName.toLowerCase() === 'iframe') {

				window.parent.postMessage({ 'cmd': 'hj_user_leave', 'msg': null }, '*');
			}
		}

		let _redirectUrl = '/';
		const _userInfo = store.getState().me.userInfo;

		if (_userInfo && [0, 1, 2].includes(_userInfo.joinType)) {

			if (_userInfo.joinType === 0) {
				_redirectUrl = '/invite';
			} else {
				_redirectUrl = '/choose';
			}
		}

		window.location = `${window.app_base_path}${_redirectUrl}`;

		if (window.config.closeTabWhenExit) {

			const userAgent = navigator.userAgent;
			if (userAgent.indexOf('Firefox') !== -1 || userAgent.indexOf('Chrome') !== -1) {

				window.location.replace('about:blank');
			} else {

				window.opener = null;
			 	window.open('', '_self');
			}
			window.close();
		} else {
			// 如果回到会议号输入界面,需要强制刷新
			const storage = window.sessionStorage;
			storage['forceReload'] = '1';
		}
	}

	_startKeyListener()
	{
		// Add keydown event listener on document
		document.addEventListener('keydown', async (event) =>
		{

			const _joined = store.getState().room.joined;

			// 按键是否一直被按住
			if (event.repeat || !_joined) return;

			const key = String.fromCharCode(event.which);
			const _keyCode = event.code;

			const source = event.target;

			const exclude = [ 'input', 'textarea' ];

			if (exclude.indexOf(source.tagName.toLowerCase()) === -1)
			{
				logger.debug('keyDown() [key:"%s",code:"%s"]', key, _keyCode);

				switch (_keyCode)
				{
					case 'Escape': // Exit large screen mode
					{
						if (store.getState().room.fullScreenConsumerId) {

							store.dispatch(roomActions.closeConsumerFullscreen());
							this._fullScreenConsumerId = null;
							store.dispatch(requestActions.notify(
								{
									text: intl.formatMessage({
										id: 'room.closeConsumerFullscreen',
										defaultMessage: 'Exit large screen mode'
									})
								}));
						}
						break;
					}

					case 'KeyA': // Activate advanced mode
					{
						store.dispatch(settingsActions.toggleAdvancedMode());
						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.toggleAdvancedMode',
									defaultMessage : 'Toggled advanced mode'
								})
							}));
						break;
					}

                    case 'Numpad1':
					case 'Digit1': // Set democratic view
					{
						if (!store.getState().room.forceMode) {

							await this.toggleLayout('autoSplit');
							store.dispatch(requestActions.notify({
								text : intl.formatMessage({
									id             : 'room.setDemocraticView',
									defaultMessage : 'Changed layout to democratic view'
								})
							}));
						} else {
							store.dispatch(requestActions.notify({
								text : intl.formatMessage({
									id             : 'room.openChairMode',
									defaultMessage : 'The host has turned on chairman mode, unable to modify the layout' // 主持人已开启主席模式,无法修改布局
								})
							}));
						}

						break;
					}

					case 'Numpad2':
					case 'Digit2': // Set filmstrip view
					{
						if (!store.getState().room.forceMode) {

							await this.toggleLayout('speaker');
							store.dispatch(requestActions.notify({
								text : intl.formatMessage({
									id             : 'room.setFilmStripView',
									defaultMessage : 'Changed layout to filmstrip view'
								})
							}));
						} else {
							store.dispatch(requestActions.notify({
								text : intl.formatMessage({
									id             : 'room.openChairMode',
									defaultMessage : 'The host has turned on chairman mode, unable to modify the layout' // 主持人已开启主席模式,无法修改布局
								})
							}));
						}

						break;
					}

					case 'Space': // Push To Talk start
					{
						const _forceMuted = store.getState().me.forceMuted;
						// 如果被强制静音了，则不响应 空格键
						logger.debug('keyDown() forceMuted', _forceMuted);
						if (!_forceMuted) {

							if (!this._micProducer) { // 启动 mic 对象

								await this.updateMic({ start: true })
							}

							if (this._micProducer.paused) {

								this.unmuteMic();
							}
						}

						break;
					}

					case 'KeyM': // Toggle microphone
					{   // 如果被强制静音了，需要先举手申请

						const { forceMuted, applyUnMuted, allowUnMuted } = store.getState().me;

						if (forceMuted) {

							if (!applyUnMuted && !allowUnMuted) {
								// 发出申请
							    await this.requestRaiseHand(true);
							} else if (applyUnMuted && !allowUnMuted) {
								// 取消申请
							    await this.requestRaiseHand(false);
							}
						} else {
							if (this._micProducer)
							{
								if (!this._micProducer.paused)
								{
									this.muteMic();

									store.dispatch(requestActions.notify(
										{
											text : intl.formatMessage({
												id             : 'devices.microphoneMute',
												defaultMessage : 'Muted your microphone'
											})
										}));
								}
								else
								{
									this.unmuteMic();

									store.dispatch(requestActions.notify(
										{
											text : intl.formatMessage({
												id             : 'devices.microphoneUnMute',
												defaultMessage : 'Unmuted your microphone'
											})
										}));
								}
							}
							else
							{
								await this.updateMic({ start: true }); // 开启音频设备
								await this.unmuteMic();   // 取消静音

								logger.debug('KeyM: ', this._micProducer);

								store.dispatch(requestActions.notify(
									{
										text : intl.formatMessage({
											id             : 'devices.microphoneEnable',
											defaultMessage : 'Enabled your microphone'
										})
									}));
							}
						}

						break;
					}

					case 'KeyV': // Toggle video
					{
						this._queue.push(async () => {

							if (this._webcamProducer)

								await this.disableWebcam('KeyV');
							else {

								await this.updateWebcam({ start: true });
							}
						}).catch((err) => {
							logger.error('开启/关闭本地视频流失败 ["%o"]', err);
						});

						break;
					}
					/*
					case 'H': // Open help dialog
					{
						store.dispatch(roomActions.setHelpOpen(true));

						break;
					}
					*/
					default:
					{
						break;
					}
				}
			}
		});
		// 只处理 Space 键
		document.addEventListener('keyup', (event) =>
		{
			const key = String.fromCharCode(event.which);
			const _keyCode = event.code;

			const source = event.target;

			const exclude = [ 'input', 'textarea' ];

			if (exclude.indexOf(source.tagName.toLowerCase()) === -1)
			{
				logger.debug('keyUp() [key:"%s", code:"%s"]', key, _keyCode);

				switch (_keyCode)
				{
					case 'Space': // Push To Talk stop
					{
						if (this._micProducer && !this._micProducer.paused)
						{
							this.muteMic();
						}

						break;
					}
					default:
					{
						break;
					}
				}
			}
			// 阻止浏览器的默认行为
			event.preventDefault();
		}, true);
	}

	_startDevicesListener()
	{
		if (!navigator.mediaDevices)
			return;

		navigator.mediaDevices.addEventListener('devicechange', async () =>
		{
			logger.debug('_startDevicesListener() | navigator.mediaDevices.ondevicechange');

			const devices = await navigator.mediaDevices.enumerateDevices();
			// logger.debug(`当前设备信息：${JSON.stringify(devices)}`);

			await this._updateAudioDevices();
			await this._updateWebcams();
			await this._updateAudioOutputDevices();

			store.dispatch(requestActions.notify(
				{
					text : intl.formatMessage({
						id             : 'devices.devicesChanged',
						defaultMessage : 'Your devices changed, configure your devices in the settings dialog'
					})
				}));
		});
	}

	/**
	 *
	 * @param {cn\en} locale
	 */
	setLocale(locale) {

		let _switchType, _lang, _messages;

		if (locale) {

			_switchType = '手动';
		} else {

			locale = getCookies('lang'); // 需要从 cookies 中读取当前语言
			if (locale) {

				_switchType = '缓存';
			} else {

				_switchType = '自动';
				locale = locales.detect();
			}
		}

		setCookies('lang', locale);

		const one = locales.loadOne(locale);
		_lang = one.locale[0];
		_messages = one.messages;

		logger.debug(`当前语言环境: locale:${locale} _lang:${_lang} -> ${_switchType}`);

		store.dispatch(intlActions.updateIntl({
			lang     : _lang,
			messages : _messages,
			list     : locales.getList()
		}));

		intl = createIntl({
			locale: _lang,
			messages: _messages
		});

		document.documentElement.lang = _lang.toLowerCase();

		//document.title = intl.formatMessage({ id: 'label.appName', defaultMessage: 'huijian' });

		document.title = window.config.appName;
	}

	login(roomId = this._roomId)
	{
		const url = `/auth/login?peerId=${this._peerId}&roomId=${roomId}`;

		window.open(url, 'loginWindow');
	}

	receiveLoginChildWindow(data)
	{
		logger.debug('receiveFromChildWindow() | [data:"%o"]', data);

		const { displayName, picture } = data;

		store.dispatch(settingsActions.setDisplayName(displayName));
		store.dispatch(meActions.setPicture(picture));

		store.dispatch(meActions.setLoggedIn(true));

		store.dispatch(requestActions.notify(
			{
				text : intl.formatMessage({
					id             : 'room.loggedIn',
					defaultMessage : 'You are logged in'
				})
			}));
	}

	logout(roomId = this._roomId)
	{

		window.open(`/auth/logout?peerId=${this._peerId}&roomId=${roomId}`, 'logoutWindow');
	}

	reset() {

		store.dispatch(meActions.setUserInfo({}));    // 清空用户 token 与 password
		store.dispatch(meActions.setLoggedIn(false));
		window.location = `${window.app_base_path}/login`;
	}

	receiveLogoutChildWindow()
	{
		logger.debug('receiveLogoutChildWindow()');

		let _userInfo = store.getState().me.userInfo;
		delete _userInfo.token;
		delete _userInfo.password;

		store.dispatch(meActions.setPicture(null));
		store.dispatch(meActions.setUserInfo(_userInfo));  // 清空用户 token 与 password
		store.dispatch(meActions.setLoggedIn(false));

		store.dispatch(requestActions.notify(
			{
				text : intl.formatMessage({
					id             : 'room.loggedOut',
					defaultMessage : 'You are logged out'
				})
			}));

		//this.close();
	}

	_soundNotification()
	{
		const { notificationSounds } = store.getState().settings;

		if (notificationSounds)
		{
			const alertPromise = this._soundAlert.play();

			if (alertPromise !== undefined)
			{
				alertPromise
					.then()
					.catch((error) =>
					{
						logger.error('_soundAlert.play() [error:"%o"]', error);
					});
			}
		}
	}

	_sendRequest(method, data)
	{
		return new Promise((resolve, reject) =>
		{
			if (!this._signalingSocket)
			{
				reject('No socket connection');
			}
			else
			{
				// ack参数,服务器响应后回调函数
				this._signalingSocket.emit(
					'request',
					{ method, data },
					this.timeoutCallback((err, response) =>
					{
						if (err) {
							reject(err);
						}
						else {
							resolve(response);
						}
					})
				);
			}
		});
	}

	/**
	 * 发送信令的通用方法
	 * @param {信令} method
	 * @param {参数} data
	 * @returns
	 */
	async sendRequestV2(method, data) {
		if (!['getTransportStats', 'getProducerStats', 'getConsumerStats'].includes(method)) {

			logger.info('sendRequestV2() [method:"%s", data:"%o"]', method, data);
		}

		try {
			return await this._sendRequest(method, data);
		}
		catch (error) {
			logger.warn('sendRequestV2() | [method:"%s"]: %s', method, error.message);
		}
	}

	timeoutCallback(callback)
	{
		let called = false;

		const interval = setTimeout(
			() => {
				if (called)
					return;
				called = true;
				callback(new SocketTimeoutError('Request timed out'));
			},
			ROOM_OPTIONS.requestTimeout
		);

		return (...args) => {

			if (called) {
				return;
			}
			called = true;
			clearTimeout(interval);

			callback(...args);
		};
	}

	/**
	 * 返回 Transport 统计
	 */
	async getTransportStats()
	{
		try
		{
			if (this._recvTransport)
			{
				const recv = await this.sendRequest('getTransportStats', { transportId: this._recvTransport.id });

				store.dispatch(transportActions.addTransportStats(recv, 'recv'));
			}

			if (this._sendTransport)
			{
				const send = await this.sendRequest('getTransportStats', { transportId: this._sendTransport.id });

				store.dispatch(transportActions.addTransportStats(send, 'send'));
			}
		}
		catch (error)
		{
			logger.error('getTransportStats() [error:"%o"]', error);
		}
	}

	/**
	 * producer 统计
	 */
	async getProducerStats()
	{
		try
		{
			if (this._webcamProducer)
			{
				const webcamSend = await this.sendRequest('getProducerStats', { producerId: this._webcamProducer.id });

				logger.debug(`getProducerStats  -> ${JSON.stringify(webcamSend)}`);

				store.dispatch(transportActions.addProducerStats(webcamSend, 'webcamSend'));
			}

			if (this._screenSharingProducer)
			{
				const screenSharingSend = await this.sendRequest('getProducerStats', { producerId: this._screenSharingProducer.id });

				store.dispatch(transportActions.addProducerStats(screenSharingSend, 'screenSharingSend'));
			}
		}
		catch (error)
		{
			logger.error('getProducerStats() [error:"%o"]', error);
		}
	}

	/**
	 * consumer 统计
	 */
	async getConsumerStats()
	{
		try
		{
			for (const consumer of this._consumers.values()) {

				if (consumer) {

					const consumerRecv = await this.sendRequest('getConsumerStats', { consumerId: consumer.id });

					store.dispatch(transportActions.addConsumerStats(consumerRecv, consumer.id));
				}
			}
		}
		catch (error)
		{
			logger.error('getConsumerStats() [error:"%o"]', error);
		}
	}

	/**
	 * 发送信令的通用方法
	 * @param {信令} method
	 * @param {参数} data
	 * @returns
	 */
	async sendRequest(method, data)
	{
		if (!['getTransportStats','getProducerStats','getConsumerStats'].includes(method)) {

			logger.info('sendRequest() [method:"%s", data:"%o"]', method, data);
		}

		const {
			requestRetries = 3
		} = window.config;

		for (let tries = 0; tries < requestRetries; tries++)
		{
			try
			{
				return await this._sendRequest(method, data);
			}
			catch (error)
			{
				if (
					error instanceof SocketTimeoutError &&
					tries < requestRetries
				) {
					// 如果为网络超时并尝试小于 3 次时,会再次尝试
					logger.warn('sendRequest() | timeout, retrying [method:"%s"  attempt:"%s"]', method, tries);
				}
				else
					throw error;
			}
		}
	}

	async changeDisplayName(displayName)
	{
		logger.debug('changeDisplayName() [displayName:"%s"]', displayName);

		if (!displayName) {
			displayName = 'Guest';
		}

		store.dispatch(meActions.setDisplayNameInProgress(true));

		try
		{
			await this.sendRequest('changeDisplayName', { displayName });

			/** 修改 me.userInfo 中的信息 */
			const _userInfo = store.getState().me.userInfo;
			store.dispatch(meActions.setUserInfo({ ..._userInfo, nickname: displayName }));

			store.dispatch(settingsActions.setDisplayName(displayName));
			store.dispatch(peerActions.setPeerDisplayName(displayName, this.peerId));

			// 同时需要修改 room.layoutConfig 中的数据
			const _videoView = this._spotlights.layoutConfig.data.find((v) => v.peerId === this.peerId);
			if (_videoView) {

				_videoView.displayName = displayName;
				store.dispatch(roomActions.setLayoutConfig(this._spotlights.layoutConfig));
			}

			store.dispatch(requestActions.notify(
				{
					text : intl.formatMessage({
						id             : 'room.changedDisplayName',
						defaultMessage : 'Your display name changed to {displayName}'
					}, {
						displayName
					})
				}));
		}
		catch (error)
		{
			logger.error('changeDisplayName() [error:"%o"]', error);

			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : intl.formatMessage({
						id             : 'room.changeDisplayNameError',
						defaultMessage : 'An error occurred while changing your display name'
					})
				}));
		}

		store.dispatch(meActions.setDisplayNameInProgress(false));
	}

	/**
	 * 修改 peerId
	 * @param {*} peerId
	 */
	async changePeerId(peerId) {

		this.oldPeerId = this.peerId;
		this.peerId = peerId;

		logger.debug('changePeerId() [oldPeerId:%s peerId]', this.oldPeerId, this.peerId);

		try
		{

			store.dispatch(peerVolumeActions.removePeerVolume(this.oldPeerId));
			store.dispatch(peerVolumeActions.setPeerVolume(this.peerId, -100));
			store.dispatch(meActions.setMyPeerId(this.peerId));
		}
		catch (error)
		{
			logger.error('changePeerId() [error:"%o"]', error);
		}
	}

    /**
	 * bingofree - 添加
	 * @param {申请/放弃} ishandup
	 * @param {0: 主动放弃; 1: 被动放弃} auto
	 */
	async requestRaiseHand(ishandup, auto = 0) {


		logger.debug('requestRaiseHand: ', ishandup);

		store.dispatch(meActions.setApplyUnMutedInProgress(true));

		try {

			await this.sendRequest('webappRaiseHand', { ishandup, auto });

			store.dispatch(meActions.setApplyUnMuted(ishandup));
			store.dispatch(meActions.setRaisedHand(ishandup));

		} catch (error) {

			logger.error('requestRaiseHand() [error:"%o"]', error);
			// We need to refresh the component for it to render changed state
			store.dispatch(meActions.setRaisedHand(!ishandup));
		}

		store.dispatch(meActions.setApplyUnMutedInProgress(false));
	}

    /**
	 * 请求共享 bingofree - 添加
	 */
	async applyShare() {

		logger.debug('applyShare()');
		try {

			await this.sendRequest('applyShare');
		} catch (error) {

			logger.error('applyShare() [error:"%o"]', error);
		}
	}

	async replyApplyShare(isok) {

		logger.debug('replyApplyShare()');
		try {

			if (this._applySharePeerId) {

				if (isok) {
					await this.disableScreenSharing(); // 关掉自己的共享请求
				}
				await this.sendRequest('replyApplyShare', {peerId: this._applySharePeerId, isok});
			}
		} catch (error) {

			logger.error('replyApplyShare() [error:"%o"]', error);
		}
	}

	async changePicture(picture)
	{
		logger.debug('changePicture() [picture: "%s"]', picture);

		try
		{
			await this.sendRequest('changePicture', { picture });
		}
		catch (error)
		{
			logger.error('changePicture() [error:"%o"]', error);
		}
	}

	async sendChatMessage(chatMessage)
	{
		logger.debug('sendChatMessage() [chatMessage:"%s"]', chatMessage);

		try
		{
			store.dispatch(chatActions.addUserMessage(chatMessage.text));

			await this.sendRequest('chatMessage', { chatMessage });
		}
		catch (error)
		{
			logger.error('sendChatMessage() [error:"%o"]', error);

			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : intl.formatMessage({
						id             : 'room.chatError',
						defaultMessage : 'Unable to send chat message'
					})
				}));
		}
	}

	saveFile(file)
	{
		file.getBlob((err, blob) =>
		{
			if (err)
			{
				store.dispatch(requestActions.notify(
					{
						type : 'error',
						text : intl.formatMessage({
							id             : 'filesharing.saveFileError',
							defaultMessage : 'Unable to save file'
						})
					}));

				return;
			}

			saveAs(blob, file.name);
		});
	}

	handleDownload(magnetUri)
	{
		store.dispatch(fileActions.setFileActive(magnetUri));

		const existingTorrent = this._webTorrent.get(magnetUri);

		if (existingTorrent)
		{
			// Never add duplicate torrents, use the existing one instead.
			this._handleTorrent(existingTorrent);

			return;
		}

		this._webTorrent.add(magnetUri, this._handleTorrent);
	}

	_handleTorrent(torrent)
	{
		// Torrent already done, this can happen if the
		// same file was sent multiple times.
		if (torrent.progress === 1)
		{
			store.dispatch(
				fileActions.setFileDone(
					torrent.magnetURI,
					torrent.files
				));

			return;
		}

		let lastMove = 0;

		torrent.on('download', () =>
		{
			if (Date.now() - lastMove > 1000)
			{
				store.dispatch(
					fileActions.setFileProgress(
						torrent.magnetURI,
						torrent.progress
					));

				lastMove = Date.now();
			}
		});

		torrent.on('done', () =>
		{
			store.dispatch(
				fileActions.setFileDone(
					torrent.magnetURI,
					torrent.files
				));
		});
	}

	async shareFiles(files)
	{
		store.dispatch(requestActions.notify(
			{
				text : intl.formatMessage({
					id             : 'filesharing.startingFileShare',
					defaultMessage : 'Attempting to share file'
				})
			}));

		createTorrent(files, (err, torrent) =>
		{
			if (err)
			{
				store.dispatch(requestActions.notify(
					{
						type : 'error',
						text : intl.formatMessage({
							id             : 'filesharing.unableToShare',
							defaultMessage : 'Unable to share file'
						})
					}));

				return;
			}

			const existingTorrent = this._webTorrent.get(torrent);

			if (existingTorrent)
			{
				store.dispatch(requestActions.notify(
					{
						text : intl.formatMessage({
							id             : 'filesharing.successfulFileShare',
							defaultMessage : 'File successfully shared'
						})
					}));

				store.dispatch(fileActions.addFile(
					this._peerId,
					existingTorrent.magnetURI
				));

				this._sendFile(existingTorrent.magnetURI);

				return;
			}

			this._webTorrent.seed(
				files,
				{ announceList: [ [ this._tracker ] ] },
				(newTorrent) =>
				{
					store.dispatch(requestActions.notify(
						{
							text : intl.formatMessage({
								id             : 'filesharing.successfulFileShare',
								defaultMessage : 'File successfully shared'
							})
						}));

					store.dispatch(fileActions.addFile(
						this._peerId,
						newTorrent.magnetURI
					));

					this._sendFile(newTorrent.magnetURI);
				});
		});
	}

	// { file, name, picture }
	async _sendFile(magnetUri)
	{
		logger.debug('sendFile() [magnetUri:"%o"]', magnetUri);

		try
		{
			await this.sendRequest('sendFile', { magnetUri });
		}
		catch (error)
		{
			logger.error('sendFile() [error:"%o"]', error);

			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : intl.formatMessage({
						id             : 'filesharing.unableToShare',
						defaultMessage : 'Unable to share file'
					})
				}));
		}
	}

	async muteMic()
	{
		logger.debug('muteMic()');

		this._talkingTimes = 0;
		this._startSpeakingTime = null;

		if (this._micProducer) {

			this._micProducer.pause();
		}

		try
		{
			if (this._micProducer) {

				await this.sendRequest('pauseProducer', { producerId: this._micProducer.id });
				store.dispatch(producerActions.setProducerPaused(this._micProducer.id));
			}
			store.dispatch(settingsActions.setAudioMuted(true));
			store.dispatch(peerActions.setPeerAudioMute(this.peerId, true));
		}
		catch (error)
		{
			logger.error('muteMic() [error:"%o"]', error);

			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : intl.formatMessage({
						id             : 'devices.microphoneMuteError',
						defaultMessage : 'Unable to mute your microphone'
					})
				}));
		}
	}

	async unmuteMic()
	{
		logger.debug('unmuteMic()');

		if (!this._micProducer)
		{
			await this.updateMic({ start: true });
		}
		else
		{
			this._micProducer.resume();

			try
			{
				await this.sendRequest('resumeProducer', { producerId: this._micProducer.id });

				store.dispatch(producerActions.setProducerResumed(this._micProducer.id));

				store.dispatch(settingsActions.setAudioMuted(false));
				store.dispatch(peerActions.setPeerAudioMute(this.peerId, false));
			}
			catch (error)
			{
				logger.error('unmuteMic() [error:"%o"]', error);

				store.dispatch(requestActions.notify(
					{
						type : 'error',
						text : intl.formatMessage({
							id             : 'devices.microphoneUnMuteError',
							defaultMessage : 'Unable to unmute your microphone'
						})
					}));
			}
		}
	}

	/**
	 * 自由/演讲者模式切换布局
	 * @param {*} mode
	 */
	async toggleLayout(mode) {

		const _layoutConfig = clone(this.viewsLayout[mode]);

		await this.initLayoutConfig(_layoutConfig, mode);

		logger.debug('切换布局并初始化配置结束: ', mode, JSON.stringify(this._spotlights.layoutConfig));
	}

	/**
	 * Updated consumers based on layoutConfig(内容没有修改)
	 * @param {当前布局配置数据} layoutConfig
	 */
	async updateConsumers(layoutConfig) {

		const _layoutConfig = clone(layoutConfig);

		this._queue.push(async () => {

			try {

				const _startTime = Date.now();

				// 将自己从终端列表中删除
				const _spotlightPeerIds = Array.from(new Set(_layoutConfig.data.map((v) => v.peerId).filter(peerId => peerId && peerId !== this.peerId))).sort();

				const _spotlights = _layoutConfig.data.
				map((v) => {
					return {
						peerId       : v.peerId,
						spatialLayer : v.spatialLayer
					}
				}).filter((item) => {
					return !['share','local'].includes(item.source) && item.peerId
				});

				logger.debug('updateConsumers() [layoutConfig:%o _spotlightPeerIds:%o _spotlights:%o]', _layoutConfig, _spotlightPeerIds, _spotlights);

				// 处理新窗口与最大化窗口
				const { 
					windowPeerId, 
					windowConsumerId, 
					fullScreenConsumerId 
				} = store.getState().room;

				// bingofree 添加 请求服务端创建 consumers
				if (windowPeerId) {

					_spotlightPeerIds.push(windowPeerId);
				}

				if (
					JSON.stringify(this._spotlightPeerIds) !== JSON.stringify(_spotlightPeerIds) ||
					this._fullScreenConsumerId !== fullScreenConsumerId) {

					await this.sendRequest('replaceConsumers', {
						spotlightPeerIds  : _spotlightPeerIds,
						spotlights        : _spotlights,
						windowConsumerId,
						fullScreenConsumerId
					});

					this._spotlightPeerIds = _spotlightPeerIds;
					this._fullScreenConsumerId = fullScreenConsumerId;

					for (const consumer of this._consumers.values()) {

						if (consumer.kind === 'video') {

							if (_spotlightPeerIds.includes(consumer.appData.peerId) || [windowConsumerId, this._fullScreenConsumerId].includes(consumer.id)) {

								await this._resumeConsumer(consumer);
							}
						}
					}
				}

				const _endTime = Date.now();
				logger.debug('执行时长 updateConsumers [ %s 毫秒]',(_endTime - _startTime));

			} catch (error) {

				logger.error('updateConsumers() [error:"%o"]', error);
			}

		}).catch((err) => {

			logger.error('updateConsumers失败 ["%o"]', err);
		});

		//return _layoutConfig;
	}

	disconnectLocalHark()
	{
		logger.debug('disconnectLocalHark()');

		if (this._harkStream != null)
		{
			let [ track ] = this._harkStream.getAudioTracks();

			track.stop();
			track = null;

			this._harkStream = null;
		}

		if (this._hark != null)
			this._hark.stop();
	}

	connectLocalHark(track)
	{
		logger.debug('connectLocalHark() [peerId:"%s", track:"%o"]', this._peerId, track);

		this._harkStream = new MediaStream();

		const newTrack = track.clone();

		this._harkStream.addTrack(newTrack);

		newTrack.enabled = true;

		this._hark = hark(this._harkStream,
			{
				play      : false,
				interval  : 10, // 毫秒
				threshold : store.getState().settings.noiseThreshold,
				history   : 100
			});

		this._hark.lastVolume = -100;  // 静音

		this._hark.on('volume_change', (volume) =>
		{
			// Update only if there is a bigger diff (取绝对值)
			if (this._micProducer && Math.abs(volume - this._hark.lastVolume) > 0.5)
			{
				// Decay calculation: keep in mind that volume range is -100 ... 0 (dB)
				// This makes decay volume fast if difference to last saved value is big
				// and slow for small changes. This prevents flickering volume indicator
				// at low levels
				// 差值大,则音量衰减快,变化小,则音量衰减慢
				if (volume < this._hark.lastVolume)  // 衰减计算
				{
					volume =
						this._hark.lastVolume -
						Math.pow(
							(volume - this._hark.lastVolume) /
							(100 + this._hark.lastVolume)
							, 2
						) * 10;
				}

				this._hark.lastVolume = volume;

				store.dispatch(peerVolumeActions.setPeerVolume(this._peerId, volume));
			}
		});

		// 如果检测到的音量为 -50 分贝, 则确定为语音, 系统最响亮的声音为 0 dB
		this._hark.on('speaking', () =>
		{
			// 如果需要静音提示,则走下面的逻辑
			if (window.config.mutedPrompt) {

				if (this._talkingTimes === 0) {

					if (this._startSpeakingTime === null) {
						this._startSpeakingTime = Date.now()
					} else {
						const _currTime = Date.now();
						if (_currTime - this._startSpeakingTime > 2 * 1000) {

							logger.debug(`我正在说话`);

							store.dispatch(meActions.setIsSpeaking(true));

							this._talkingTimes = 1;
							this._startSpeakingTime = Date.now();

							setTimeout(() => {
								store.dispatch(meActions.setIsSpeaking(false));
							}, 3 * 1000)
						}
					}

				} else if (this._talkingTimes === 1) {

					const _currTime = Date.now();

					if (_currTime - this._startSpeakingTime > 20 * 60 * 1000) {

						logger.debug(`我正在说话`);

						store.dispatch(meActions.setIsSpeaking(true));

						this._startSpeakingTime = Date.now();

						setTimeout(() => {
							store.dispatch(meActions.setIsSpeaking(false));
						}, 3 * 1000)

					}
				}
			}
			//triggerSpeaking();

			if ((store.getState().settings.voiceActivatedUnmute ||
				store.getState().me.isAutoMuted) &&
				this._micProducer && this._micProducer.paused) {

					this._micProducer.resume();
			}

			store.dispatch(meActions.setAutoMuted(false)); // sanity action
		});

		this._hark.on('stopped_speaking', () =>
		{
			//store.dispatch(meActions.setIsSpeaking(false));

			if (
				store.getState().settings.voiceActivatedUnmute &&
				this._micProducer &&
				!this._micProducer.paused
			)
			{
				this._micProducer.pause();

				store.dispatch(meActions.setAutoMuted(true));
			}
		});
	}

	async changeAudioOutputDevice(deviceId)
	{
		logger.debug('changeAudioOutputDevice() [deviceId:"%s"]', deviceId);

		store.dispatch(meActions.setAudioOutputInProgress(true));

		try
		{
			const device = this._audioOutputDevices[deviceId];

			if (!device)
				throw new Error('Selected audio output device no longer available');

			store.dispatch(settingsActions.setSelectedAudioOutputDevice(deviceId));

			await this._updateAudioOutputDevices();
		}
		catch (error)
		{
			logger.error('changeAudioOutputDevice() [error:"%o"]', error);
		}

		store.dispatch(
			meActions.setAudioOutputInProgress(false));
	}

	// Only Firefox supports applyConstraints to audio tracks
	// See: 开启音频
	// https://bugs.chromium.org/p/chromium/issues/detail?id=796964
	async updateMic({
		start = false,
		paused = false,
		restart = false || this._device.flag !== 'firefox',
		newDeviceId = null
	} = {}) {

		logger.debug(
			'updateMic() [start:"%s", paused:"%s", restart:"%s", newDeviceId:"%s"]',
			start,
			paused,
			restart,
			newDeviceId
		);

		let track;

		try {
			if (!this._mediasoupDevice.canProduce('audio')) {
				throw new Error('cannot produce audio');
			}

			if (newDeviceId && !restart) {
				throw new Error('changing device requires restart');
			}

			if (newDeviceId) {
				store.dispatch(settingsActions.setSelectedAudioDevice(newDeviceId));
			}

			store.dispatch(meActions.setAudioInProgress(true));

			const deviceId = await this._getAudioDeviceId();
			const device = this._audioDevices[deviceId];

			if (!device) {
				throw new Error('no audio devices');
			}

			const {
				sampleRate,
				channelCount,
				volume,
				autoGainControl,
				echoCancellation,
				noiseSuppression,
				sampleSize,
				audioMuted
			} = store.getState().settings;  // 获取当前音频配置数据

			if (
				(restart && this._micProducer) ||
				start
			) {
				this.disconnectLocalHark();

				if (this._micProducer) {
					await this.disableMic();
				}

				const stream = await navigator.mediaDevices.getUserMedia({
					audio : {
						deviceId : { ideal: deviceId },
						sampleRate,
						channelCount,
						volume,
						autoGainControl,
						echoCancellation,
						noiseSuppression,
						sampleSize
					}
				});

				([ track ] = stream.getAudioTracks());

				const { deviceId: trackDeviceId } = track.getSettings();

				store.dispatch(settingsActions.setSelectedAudioDevice(trackDeviceId));

				this._micProducer = await this._sendTransport.produce({
						track,
						codecOptions :
						{
							opusStereo          : false,
							opusDtx             : true,
							opusFec             : true,
							opusPtime           : '3',
							opusMaxPlaybackRate	: 48000
						},
						appData :
						{
							source: 'mic',
							paused
						}
				});

				store.dispatch(producerActions.addProducer({
					id            : this._micProducer.id,
					source        : 'mic',
					paused        : this._micProducer.paused,
					track         : this._micProducer.track,
					rtpParameters : this._micProducer.rtpParameters,
					codec         : this._micProducer.rtpParameters.codecs[0].mimeType.split('/')[1]
				}));

				logger.debug(`成功创建 micProducer [${this._micProducer.id}]`);

				//logger.debug('在主持人开启了强制静音，只有等 _micProducer 创建之后，才可以设置 mic 的状态');
				const { forceMuted, allowUnMuted } = store.getState().me;
				const { chairPeerId } = store.getState().room;

				if (forceMuted && this.peerId !== chairPeerId) {
					if (allowUnMuted) {

						await this.unmuteMic();
					} else {

						await this.muteMic();
					}
				}

				this._micProducer.on('transportclose', () =>
				{
					this._micProducer = null;
				});

				this._micProducer.on('trackended', async () =>
				{
					store.dispatch(requestActions.notify(
						{
							//type : 'error',
							text : intl.formatMessage({
								id             : 'devices.microphoneDisconnected',
								defaultMessage : 'Microphone disconnected'
							})
						}));

					await this.muteMic();	
					await this.disableMic();
					// 通知会中其它终端音频设备不可用
					await this.reportTermStatus(3, 1);
				});

				this._micProducer.volume = 0;

				this.connectLocalHark(track);

				// 如果切换前是静音状态，则恢复静音
				// audioMuted && await this.muteMic();
			}
			else if (this._micProducer)
			{
				({ track } = this._micProducer);

				await track.applyConstraints({
					sampleRate,
					channelCount,
					volume,
					autoGainControl,
					echoCancellation,
					noiseSuppression,
					sampleSize
				});

				if (this._harkStream != null)
				{
					const [ harkTrack ] = this._harkStream.getAudioTracks();

					harkTrack && await harkTrack.applyConstraints({
						sampleRate,
						channelCount,
						volume,
						autoGainControl,
						echoCancellation,
						noiseSuppression,
						sampleSize
					});
				}
			}

			if (paused) {

				await this.muteMic();
			}

			await this._updateAudioDevices();
			// 设置状态
			store.dispatch(peerActions.setPeerAudioMute(this.peerId, paused));

			const _myPeer = store.getState().peers[this.peerId];
			if (_myPeer && !_myPeer.audioAvailable) {

				// 音频设备可用
				await this.reportTermStatus(3, 2);
			}
		}
		catch (error)
		{
			logger.error('updateMic() [error:"%o"]', error);

			store.dispatch(settingsActions.setAudioMuted(true));
			store.dispatch(peerActions.setPeerAudioMute(this.peerId, true));

			// 通知会中其它终端音频设备不可用
			await this.reportTermStatus(3, 1);

			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : intl.formatMessage({
						id             : 'devices.microphoneError',
						defaultMessage : 'An error occurred while accessing your microphone'
					})
				}));

			if (track) {
				track.stop();
			}
		}

		store.dispatch(meActions.setAudioInProgress(false));
	}

	async updateWebcam({
		init = false,
		start = false,
		restart = false,
		newDeviceId = null,
		newResolution = null,
		newFrameRate = null,
		needSetVideoMuted = true,
	} = {}) {

		logger.debug(
			'updateWebcam() [start:"%s", restart:"%s", newDeviceId:"%s", newResolution:"%s", newFrameRate:"%s"]',
			start,
			restart,
			newDeviceId,
			newResolution,
			newFrameRate
		);

		let track;
		try {
			if (!this._mediasoupDevice.canProduce('video')) {
				throw new Error('cannot produce video');
			}
			if (newDeviceId && !restart) {
				throw new Error('changing device requires restart');
			}
			if (newDeviceId) {

				store.dispatch(settingsActions.setSelectedWebcamDevice(newDeviceId));
			}
			if (newResolution) {

				store.dispatch(settingsActions.setVideoResolution(newResolution));
			}
			if (newFrameRate) {

				store.dispatch(settingsActions.setVideoFrameRate(newFrameRate));
			}

			const { videoMuted } = store.getState().settings;

			if (init && videoMuted) {
				return;
			}
			else if (needSetVideoMuted) {
				
				store.dispatch(settingsActions.setVideoMuted(false));
			}

			store.dispatch(meActions.setWebcamInProgress(true));

			const deviceId = await this._getWebcamDeviceId();
			const device = this._webcams[deviceId];

			if (!device) {

				throw new Error('no webcam devices');
			}

			const { 
				resolution, 
				frameRate 
			} = store.getState().settings;

			logger.debug('分辨率:', resolution);

			if ((restart && this._webcamProducer) || start) {

				// 开始记时
				let _startTime = Date.now();

				if (this._webcamProducer) {

					await this.disableWebcam('updateWebcam');
				}

				logger.debug(`方法:[updateWebcam -> disableWebcam] 执行时间: ${(Date.now() - _startTime)} 毫秒`);
				_startTime = Date.now();

				let stream = await navigator.mediaDevices.getUserMedia({
					video: {
						deviceId: { ideal: deviceId },
						...VIDEO_CONSTRAINS[resolution],
						frameRate
					}
				});

				logger.debug(`方法:[updateWebcam -> getUserMedia] 执行时间: ${(Date.now() - _startTime)} 毫秒`);
				_startTime = Date.now();

				const { 
					virtualRealityEnabled, 
					virtualBgUrl 
				} = store.getState().me;

				if (virtualRealityEnabled && virtualBgUrl) {
					// 这里对视频进行处理
					this.virtualReality.isVBg = true;
					stream = await this.virtualReality.transformMediaStream(stream);
					//stream = await this.virtualReality.startVideoEffect(stream);
				}

				logger.debug(`方法:[updateWebcam -> transformMediaStream] 执行时间: ${(Date.now() - _startTime)} 毫秒`);
				_startTime = Date.now();

				([ track ] = stream.getVideoTracks());

				if (this._useSimulcast) { // 多播
					//debugger;
					// If VP9 is the only available video codec then use SVC.
					const firstVideoCodec = this._mediasoupDevice
						.rtpCapabilities
						.codecs
						.find((c) => c.kind === 'video');

					let encodings;

					if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9')
						encodings = VIDEO_KSVC_ENCODINGS;
					else if ('simulcastEncodings' in window.config)
						encodings = window.config.simulcastEncodings;
					else
						encodings = VIDEO_SIMULCAST_ENCODINGS;

					logger.debug(`视频采集参数: ${JSON.stringify(encodings)}`);
					// 此方法执行时可能出错
					this._webcamProducer = await this._sendTransport.produce({
						track,
						encodings,
						disableTrackOnPause: false,
						zeroRtpOnPause: true,
						codecOptions: {
							videoGoogleStartBitrate: 1000
						},
						appData: {
							source: 'webcam'
						}
					});

					logger.debug(`方法:[updateWebcam -> createWebcamProducer] 执行时间: ${(Date.now() - _startTime)} 毫秒`);
					_startTime = Date.now();

				} else {
					
					this._webcamProducer = await this._sendTransport.produce({
						track,
						disableTrackOnPause: false,
						zeroRtpOnPause: true,
						appData: {
							source: 'webcam'
						}
					});
				}

				if (!this._webcamProducer.paused) {
					
					logger.debug('this._webcamProducer 初始化暂停');
					this._webcamProducer.pause();
				}

				store.dispatch(producerActions.addProducer({
					id            : this._webcamProducer.id,
					source        : 'webcam',
					paused        : this._webcamProducer.paused,
					track         : this._webcamProducer.track,
					rtpParameters : this._webcamProducer.rtpParameters,
					codec         : this._webcamProducer.rtpParameters.codecs[0].mimeType.split('/')[1]
				}));

				this._webcamProducer.on('transportclose', () => {
					
					this._webcamProducer = null;
				});

				this._webcamProducer.on('trackended', async () => {
					/*
					store.dispatch(requestActions.notify({
						//type : 'error',
						text : intl.formatMessage({
							id             : 'devices.cameraDisconnected',
							defaultMessage : 'Camera disconnected'
						})
					}));
					*/
					await this.disableWebcam('trackended');
					// 通知会中其它终端视频设备不可用
					await this.reportTermStatus(4, 0);

				});
			} else if (this._webcamProducer) {

				({ track } = this._webcamProducer);

				await track.applyConstraints(
					{
						...VIDEO_CONSTRAINS[resolution],
						frameRate
					}
				);

				// Also change resolution of extra video producers
				for (const producer of this._extraVideoProducers.values()) {
					({ track } = producer);

					await track.applyConstraints(
						{
							...VIDEO_CONSTRAINS[resolution],
							frameRate
						}
					);
				}
			}

			await this._updateWebcams();

			if (needSetVideoMuted) {

				await this._spotlights.updatePeerVideoMute(this.peerId, false, 'updateWebcam');
			}

			const _myPeer = store.getState().peers[this.peerId];
			if (_myPeer && !_myPeer.videoAvailable) {

				// 视频设备可用
				await this.reportTermStatus(4, 1);
			}

		} catch (error) {

			logger.error('updateWebcam() [error:"%o"]', error)
			// 修改本地状态值
			store.dispatch(settingsActions.setVideoMuted(true));
			await this._spotlights.updatePeerVideoMute(this.peerId, true, 'updateWebcam')

			// 如果访问相机时发生错误，则通知会见，摄像头关闭了
			await this.sendRequest('cameraError')

			// 通知会中其它终端视频设备不可用
			await this.reportTermStatus(4, 0);

			store.dispatch(requestActions.notify({
				type : 'error',
				text : intl.formatMessage({
					id             : 'devices.cameraError',
					defaultMessage : 'An error occurred while accessing your camera'
				})
			}));

			if (track) {
				track.stop();
			}
		}

		store.dispatch(meActions.setWebcamInProgress(false));
	}

	async promoteAllLobbyPeers()
	{
		logger.debug('promoteAllLobbyPeers()');

		store.dispatch(
			roomActions.setLobbyPeersPromotionInProgress(true));

		try
		{
			await this.sendRequest('promoteAllPeers');
		}
		catch (error)
		{
			logger.error('promoteAllLobbyPeers() [error:"%o"]', error);
		}

		store.dispatch(
			roomActions.setLobbyPeersPromotionInProgress(false));
	}

	async promoteLobbyPeer(peerId)
	{
		logger.debug('promoteLobbyPeer() [peerId:"%s"]', peerId);

		store.dispatch(
			lobbyPeerActions.setLobbyPeerPromotionInProgress(peerId, true));

		try
		{
			await this.sendRequest('promotePeer', { peerId });
		}
		catch (error)
		{
			logger.error('promoteLobbyPeer() [error:"%o"]', error);
		}

		store.dispatch(
			lobbyPeerActions.setLobbyPeerPromotionInProgress(peerId, false));
	}

	async clearChat()
	{
		logger.debug('clearChat()');

		store.dispatch(
			roomActions.setClearChatInProgress(true));

		try
		{
			await this.sendRequest('moderator:clearChat');

			store.dispatch(chatActions.clearChat());
		}
		catch (error)
		{
			logger.error('clearChat() [error:"%o"]', error);
		}

		store.dispatch(
			roomActions.setClearChatInProgress(false));
	}

	async clearFileSharing()
	{
		logger.debug('clearFileSharing()');

		store.dispatch(
			roomActions.setClearFileSharingInProgress(true));

		try
		{
			await this.sendRequest('moderator:clearFileSharing');

			store.dispatch(fileActions.clearFiles());
		}
		catch (error)
		{
			logger.error('clearFileSharing() [error:"%o"]', error);
		}

		store.dispatch(
			roomActions.setClearFileSharingInProgress(false));
	}

	async kickPeer(peerId)
	{
		logger.debug('kickPeer() [peerId:"%s"]', peerId);

		store.dispatch(
			peerActions.setPeerKickInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:kickPeer', { peerId });
		}
		catch (error)
		{
			logger.error('kickPeer() [error:"%o"]', error);
		}

		store.dispatch(
			peerActions.setPeerKickInProgress(peerId, false));
	}

	async mutePeer(peerId)
	{
		logger.debug('mutePeer() [peerId:"%s"]', peerId);

		store.dispatch(
			peerActions.setMutePeerInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:mute', { peerId });
		}
		catch (error)
		{
			logger.error('mutePeer() [error:"%o"]', error);
		}

		store.dispatch(
			peerActions.setMutePeerInProgress(peerId, false));
	}

	async stopPeerVideo(peerId)
	{
		logger.debug('stopPeerVideo() [peerId:"%s"]', peerId);

		store.dispatch(
			peerActions.setStopPeerVideoInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:stopVideo', { peerId });
		}
		catch (error)
		{
			logger.error('stopPeerVideo() [error:"%o"]', error);
		}

		store.dispatch(
			peerActions.setStopPeerVideoInProgress(peerId, false));
	}

	async stopPeerScreenSharing(peerId)
	{
		logger.debug('stopPeerScreenSharing() [peerId:"%s"]', peerId);

		store.dispatch(
			peerActions.setStopPeerScreenSharingInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:stopScreenSharing', { peerId });
		}
		catch (error)
		{
			logger.error('stopPeerScreenSharing() [error:"%o"]', error);
		}

		store.dispatch(
			peerActions.setStopPeerScreenSharingInProgress(peerId, false));
	}
	/**
	 * 主持人关掉所有参会人语音
	 */
	async muteAllPeers()
	{
		logger.debug('muteAllPeers()');

		store.dispatch(roomActions.setMuteAllInProgress(true));

		try
		{
			// 这里调用网关指令
			await this.sendRequest('moderator:chairmuteall',
			{
				peerId: this.peerId
			})

			// 同时将会中其它所有终端状态改为静音状态
			const { chairPeerId } = store.getState().room;
			const _peerList = Object.values(store.getState().peers);

			_peerList.filter((peer) => [undefined, false].includes(peer.audioMuted) && peer.id !== chairPeerId && !peer.isCohost).map(
				peer => {
					store.dispatch(
						peerActions.setPeerAudioMute(
							peer.id,
							true
						)
					)
				}
			);

			store.dispatch(requestActions.notify({
				text : intl.formatMessage({
					id             : 'label.muteAll',
					defaultMessage : 'The participants have been muted'
				})
			}));

		}
		catch (error)
		{
			logger.error('muteAllPeers() [error:"%o"]', error);
		}

		store.dispatch(roomActions.setMuteAllInProgress(false));
	}
	/**
	 * 主持人强制关掉所有参会人语音
	 */
	async forceMuteAllPeers(isForceMute)
	{
		logger.debug('forceMuteAllPeers()');

		store.dispatch(roomActions.setForceMuteAllInProgress(true));

		try
		{
			// 这里调用网关指令
			await this.sendRequest('moderator:chairforcemute', {
				peerId :this.peerId,
				isForceMute
			});
		}
		catch (error)
		{
			logger.error('forceMuteAllPeers() [error:"%o"]', error);
		}

		store.dispatch(roomActions.setForceMuteAllInProgress(false));
	}

	/**
	 * 主持人响应会中其它终端举手发言请求
	 */
	 async permitRaiseHand(permitPeerId, isPermit)
	 {
		 logger.debug('permitRaiseHand()');

		 try
		 {
			 // 这里调用网关指令
			 await this.sendRequest('moderator:chairpermitraisehand', {
				 peerId      : this.peerId,
				 permitPeerId,
				 isPermit
			 });
		 }
		 catch (error)
		 {
			 logger.error('permitRaiseHand() [error:"%o"]', error);
		 }
	 }

	 /**
	  * 主持人开始/停止录像
	  * @param {*} isRecord
	  */
	 async recordVideo(isRecord)
	 {
		 logger.debug('recordVideo()');

		 try
		 {
			 // 这里调用网关指令
			 await this.sendRequest('moderator:chairrecordvideo', {
				 peerId   : this.peerId,
				 isRecord
			 });
		 }
		 catch (error)
		 {
			 logger.error('recordVideo() [error:"%o"]', error);
		 }
	 }

	/**
	 * 将主持人权限移交给其它参会人
	 * @param {*} newChairPeerId
	 */
	 async chairTransfer(newChairPeerId)
	 {
		 logger.debug('chairTransfer()');

		 try
		 {
			 // 这里调用网关指令
			 await this.sendRequest('moderator:chairtransfer', {
				 peerId      : this.peerId,
				 newChairPeerId
			 });

			 // 如果当前终端为主持人且实时字幕弹窗打开、权限仅主持人开启
			 const { 
				subtitleOpen, 
				subtitleState,
				isForceMuteAll 
			} = store.getState().room;

			 if (subtitleState === 1 && subtitleOpen) {

				store.dispatch(roomActions.setSubtitleOpen(false));  // 关闭弹窗
			 }

			 store.dispatch(roomActions.setChairPeerId(newChairPeerId));

			 // 如果把自己的主持人权限移交给其它参会人,则不需要提示
			 store.dispatch(roomActions.setHasRecordingPrompt(false));

			 // 主持人迁走之后,恢复状态值
			 if (isForceMuteAll) {

				 const _meIsCohost = meIsCohostSelector(store.getState());

				 if (!_meIsCohost) {

					 store.dispatch(roomActions.setIsForceMuteAll(false));
				 }

				 store.dispatch(meActions.setForceMuted(true));

				 const { audioMuted } = store.getState().settings;
				 const { allowUnMuted } = store.getState().me;

				 if (!audioMuted && !allowUnMuted) {

					 store.dispatch(meActions.setAllowUnMuted(true));
				 }
				 //await this.muteMic()
			 }

			 store.dispatch(requestActions.notify({
				text : intl.formatMessage({
					id             : 'label.chairTransferred',
					defaultMessage : 'Host authority has been transferred'
				})
			}));

		 }
		 catch (error)
		 {
			 logger.error('chairTransfer() [error:"%o"]', error);
		 }
	 }

	/**
	 * 主持人设置联席主持人
	 * @param {*} cohostPeerId 联席主持人 peerId
	 * @param {*} opType 添加/删除联席主持人
	 */
	async setCohost(cohostPeerId, opType)
	{
		logger.debug('setCohost()');

		try
		{
			// 这里调用网关指令
			await this.sendRequest('moderator:setcohosts', {

				peerId      : this.peerId,
				opType,
				cohostPeerIds : [cohostPeerId]
			})
		}
		catch (error)
		{
			logger.error('setCohost() [error:"%o"]', error);
		}
	}

	 /**
	  * 主持人开启/禁止音视频
	  * @param {*} mutedPeerId
	  * @param {*} type
	  * @param {*} mute
	  */
	 async chairMute(mutedPeerId, type, mute)
	 {
		 logger.debug('chairMute()');

		 try
		 {
			// 正在操作中...
			if (type === 0) {

				store.dispatch(meActions.setChairAudioMuteInProgress(true));
			} else if (type === 1) {

				store.dispatch(meActions.setChairVideoMuteInProgress(true));
			}

			 // 这里调用网关指令
			 await this.sendRequest('moderator:chairmute', {
				 peerId    : this.peerId,
				 mutedPeerId,
				 type,
				 mute
			 });
		 }
		 catch (error)
		 {
			 logger.error('chairMute() [error:"%o"]', error);
		 }
	 }
	 /**
	  * 主持人设置/取消焦点
	  * @param {*} lockedPeerId
	  * @param {*} lock
	  */
	 async chairVideoLock(lockedPeerId, lock)
	 {
		 logger.debug('chairVideoLock()');

		 try
		 {
			 // 这里调用网关指令
			 await this.sendRequest('moderator:chairvideolock', {
				 peerId :this.peerId,
				 lockedPeerId,
				 lock
			 });
		 }
		 catch (error)
		 {
			 logger.error('chairVideoLock() [error:"%o"]', error);
		 }
	 }

	 /**
	  * 主持人结束会议
	  * @param {*} exit
	  */
	async chairEndConf(exit = true)
	 {
		 logger.debug('chairEndConf()');

		 try
		 {
			 // 这里调用网关指令
			 await this.sendRequest('moderator:chairendconf', {
				 peerId :this.peerId,
				 exit
			 });
		 }
		 catch (error)
		 {
			 logger.error('chairEndConf() [error:"%o"]', error);
		 }
	 }

	 /**
	  * 主持人开启/关闭实时字幕
	  * @param {*} opType
	  */
	 async changePermissionTranslation(opType)
	 {
		 logger.debug('changePermissionTranslation()');

		 try
		 {
			 // 这里调用网关指令
			 await this.sendRequest('moderator:changePermissionTranslation', {
				 peerId : this.peerId,
				 opType
			 });
		 }
		 catch (error)
		 {
			 logger.error('changePermissionTranslation() [error:"%o"]', error);
		 }
	 }

	 /**
	  * 通知 mc 开启/关闭实时字幕
	  * @param {*} opType
	  */
	 async webappTranslationControl(opType)
	 {
		 logger.debug('webappTranslationControl()');

		 try
		 {
			 // 这里调用网关指令
			 await this.sendRequest('webappTranslationControl', {
				 peerId : this.peerId,
				 opType
			 });
		 }
		 catch (error)
		 {
			 logger.error('webappTranslationControl() [error:"%o"]', error);
		 }
	 }

	 /**
	  * 上报设备状态(远程控制摄像头)
	  * @param {Integer} status 设备类型
	  * @param {Integer} subStatus 设备状态
	  */
	 async reportTermStatus(status, subStatus) {

		logger.debug('reportTermStatus()');

		try {

			await this.sendRequest('webappTermStatus', {
				peerId : this.peerId,
				status,
				subStatus,
				text : 'unExist'
			});

			// 改变本地设备状态
			if ([3, 4].includes(status) && [0, 1, 2].includes(subStatus)) {

				if (status === 3) {

					if (subStatus === 1) {

						store.dispatch(peerActions.setPeerAudioAvailable(this.peerId, false));  // 音频设备不可用
					} else if (subStatus === 2) {

						store.dispatch(peerActions.setPeerAudioAvailable(this.peerId, true));  // 音频设备可用
					}
				} else if (status === 4) {

					if (subStatus === 0) {

						store.dispatch(peerActions.setPeerVideoAvailable(this.peerId, false));  // 音频设备不可用
					} else if (subStatus === 1) {

						store.dispatch(peerActions.setPeerVideoAvailable(this.peerId, true));   // 音频设备可用
					}
				}
			}

		} catch (error) {

			logger.error('reportTermStatus() [error:"%o"]', error);
		}
	 }

	async stopAllPeerScreenSharing()
	{
		logger.debug('stopAllPeerScreenSharing()');

		store.dispatch(
			roomActions.setStopAllScreenSharingInProgress(true));

		try
		{
			await this.sendRequest('moderator:stopAllScreenSharing');
		}
		catch (error)
		{
			logger.error('stopAllPeerScreenSharing() [error:"%o"]', error);
		}

		store.dispatch(
			roomActions.setStopAllScreenSharingInProgress(false));
	}

	async closeMeeting()
	{
		logger.debug('closeMeeting()');

		store.dispatch(
			roomActions.setCloseMeetingInProgress(true));

		try
		{
			await this.sendRequest('moderator:closeMeeting');
		}
		catch (error)
		{
			logger.error('closeMeeting() [error:"%o"]', error);
		}

		store.dispatch(
			roomActions.setCloseMeetingInProgress(false));
	}

	async _pauseConsumer(consumer)
	{
		// logger.debug('_pauseConsumer() [consumer:"%o"]', consumer);

		if (consumer.paused || consumer.closed)
			return;

		try
		{
			await this.sendRequest('pauseConsumer', { consumerId: consumer.id });
			store.dispatch(consumerActions.setConsumerPaused(consumer.id, 'local'));
			consumer.pause();    // 其他web挂断时，这里可能报错
		}
		catch (error)
		{
			logger.warn('_pauseConsumer() [error:"%o"]', error);
		}
	}

	async _resumeConsumer(consumer)
	{
		// logger.debug('_resumeConsumer() [consumer:"%o"]', consumer);

		//if ((!consumer.paused || consumer.closed) && !consumer.appData.isFirstCreated)
		if ((!consumer.paused || consumer.closed))
			return;

		try
		{
			await this.sendRequest('resumeConsumer', { consumerId: consumer.id });

			consumer.resume();

			consumer.appData.isFirstCreated = false;

			store.dispatch(consumerActions.setConsumerResumed(consumer.id, 'local'));
		}
		catch (error)
		{
			logger.error('_resumeConsumer() [error:"%o"]', error);
		}
	}

	async lowerPeerHand(peerId)
	{
		logger.debug('lowerPeerHand() [peerId:"%s"]', peerId);

		store.dispatch(
			peerActions.setPeerRaisedHandInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:lowerHand', { peerId });
		}
		catch (error)
		{
			logger.error('lowerPeerHand() [error:"%o"]', error);
		}

		store.dispatch(
			peerActions.setPeerRaisedHandInProgress(peerId, false));
	}

	/**
	 * 此方法过期
	 * @param {*} raisedHand
	 */
	async setRaisedHand(raisedHand)
	{
		logger.debug('setRaisedHand: ', raisedHand);

		store.dispatch(
			meActions.setRaisedHandInProgress(true));

		try
		{
			await this.sendRequest('raisedHand', { raisedHand });

			store.dispatch(
				meActions.setRaisedHand(raisedHand));
		}
		catch (error)
		{
			logger.error('setRaisedHand() [error:"%o"]', error);

			// We need to refresh the component for it to render changed state
			store.dispatch(meActions.setRaisedHand(!raisedHand));
		}

		store.dispatch(meActions.setRaisedHandInProgress(false));
	}

	async setMaxSendingSpatialLayer(spatialLayer)
	{
		logger.debug('setMaxSendingSpatialLayer() [spatialLayer:"%s"]', spatialLayer);

		try
		{
			if (this._webcamProducer) {
				await this._webcamProducer.setMaxSpatialLayer(spatialLayer);
			}

			if (this._screenSharingProducer) {
				await this._screenSharingProducer.setMaxSpatialLayer(spatialLayer);
			}
		}
		catch (error)
		{
			logger.error('setMaxSendingSpatialLayer() [error:"%o"]', error);
		}
	}

	async restartIce(transport, ice, delay)
	{
		logger.debug('restartIce() [transport:%o ice:%o delay:%d]', transport, ice, delay);

		if (!transport)
		{
			logger.error('restartIce(): missing valid transport object');

			return;
		}

		if (!ice)
		{
			logger.error('restartIce(): missing valid ice object');

			return;
		}

		clearTimeout(ice.timer);
		ice.timer = setTimeout(async () =>
		{
			try
			{
				if (ice.restarting)
				{
					return;
				}
				ice.restarting = true;

				const iceParameters = await this.sendRequest(
					'restartIce',
					{ transportId: transport.id });

				await transport.restartIce({ iceParameters: (iceParameters ? iceParameters : this._iceParameters) });
				ice.restarting = false;
				logger.debug('ICE restarted');
			}
			catch (error)
			{
				logger.error('restartIce() [failed:%o]', error);

				ice.restarting = false;
				ice.timer = setTimeout(() =>
				{
					this.restartIce(transport, ice, delay * 2);
				}, delay);
			}
		}, delay);
	}

	async setConsumerPreferedLayers(consumerId, spatialLayer, temporalLayer, source )
	{
		logger.debug('setConsumerPreferedLayers() [consumerId:"%s", spatialLayer:"%s", temporalLayer:"%s", source:"%s"]', consumerId, spatialLayer, temporalLayer, source);

		try
		{
			await this.sendRequest('setConsumerPreferedLayers', { consumerId, spatialLayer, temporalLayer });

			store.dispatch(consumerActions.setConsumerPreferedLayers(consumerId, spatialLayer, temporalLayer));
		}
		catch (error)
		{
			logger.error('setConsumerPreferedLayers() [error:"%o"], source:"%s"', error, source);
		}
	}

	async setConsumerPriority(consumerId, priority)
	{
		logger.debug(
			'setConsumerPriority() [consumerId:"%s", priority:%d]',
			consumerId, priority);

		try
		{
			await this.sendRequest('setConsumerPriority', { consumerId, priority });

			store.dispatch(consumerActions.setConsumerPriority(consumerId, priority));
		}
		catch (error)
		{
			logger.error('setConsumerPriority() [error:"%o"]', error);
		}
	}

	async requestConsumerKeyFrame(consumerId)
	{
		logger.debug('requestConsumerKeyFrame() [consumerId:"%s"]', consumerId);

		try
		{
			await this.sendRequest('requestConsumerKeyFrame', { consumerId });
		}
		catch (error)
		{
			logger.error('requestConsumerKeyFrame() [error:"%o"]', error);
		}
	}

	async _loadDynamicImports()
	{
		({ default: createTorrent } = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "createtorrent" */
			'create-torrent'
		));

		({ default: WebTorrent } = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "webtorrent" */
			'webtorrent'
		));

		({ default: saveAs } = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "file-saver" */
			'file-saver'
		));

		({ default: ScreenShare } = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "screensharing" */
			'./ScreenShare'
		));

		mediasoupClient = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "mediasoup" */
			'mediasoup-client'
		);

		({ default: io } = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "socket.io" */
			'socket.io-client'
		));
	}

	async _handleShareConsumerClosed(data) {
		
		const { 
			consumerId, 
			source 
		} = data;
		
		const consumer = this._consumers.get(consumerId);

		if (consumer) {
			
			logger.debug('_handleShareConsumerClosed [consumerId:%s, source:%s] 关闭', consumerId, source);

			consumer.close();

			this._consumers.delete(consumerId);

			const { peerId } = consumer.appData;
			store.dispatch(consumerActions.removeConsumer(consumerId, peerId));

			const { 
				windowConsumerId, 
				fullScreenConsumerId,
				windowPeerId, 
				windowVideoSource 
			} = store.getState().room;

			if (windowConsumerId && windowConsumerId === consumerId) {

				store.dispatch(roomActions.setWindowConsumerId(null));
			}
			if (fullScreenConsumerId && fullScreenConsumerId === consumerId) {

				store.dispatch(roomActions.closeConsumerFullscreen());
				this._fullScreenConsumerId = null;
			}

			//停止共享后  如果有新窗口且新窗口内是共享内容 则关闭新窗口
			if (windowPeerId && windowVideoSource === 'screen') {

				store.dispatch(roomActions.closeConsumerFullscreen());
				store.dispatch(roomActions.setWindowPeerId(null));
			}

			// 标记会议室没有了共享视频
			store.dispatch(roomActions.setScreenSharing(false));
			await this._spotlights.handleShare(null);

		} else {

			logger.warn('_handleShareConsumerClosed [consumerId:%s, source:%s] 不存在', consumerId, source);
		}
	}

	/**
	 * 判断聚光灯下面的track 的 muted 是否为 true 超过 3 次
	 * @returns
	 */
	hasTrackMuted() {

		if(this._mutedCount === undefined) {
			this._mutedCount = 0;
		}

		for(let i = 0; i < this._spotlightPeerIds.length; i++) {
			const peerId = this._spotlightPeerIds[i];
			for (const _consumer of this._consumers.values()) {
				if(_consumer.appData.peerId === peerId && _consumer.track.muted) {
					this._mutedCount++;
					if(this._mutedCount > 3) {
						this._mutedCount = 0;
						return false;
					}
					return true;
				}
			}
		}

		this._mutedCount = 0;
		return false;
	}


	/**
	 *  重新协商(判断track.muted 是否为 true 超过 3 次)
	 * @param {*} data: { id, producerId, producerPeerId } 必须有这 3 个字段
	 * @returns 重协商创建 consumer，则返回 true；反之返回 false
	 */
	handleTrackMuted(data) {
		const THREDHOLD_COUNT = 7;
		const THREDHOLD_INTERVAL = 6600;

		const { id, producerId, prevPeerId, producerPeerId,  /* 生产者 peerId */ qlevel, appData, fromActiveSpeaker } = data;

		if(this._mutedTrackCounts === undefined) {
			this._mutedTrackCounts = {};
		}

		const _consumer = this._consumers.get(id);
		if(!_consumer) {
			if(this._mutedTrackCounts.hasOwnProperty(id)) {
				delete this._mutedTrackCounts[id];
			}
			return false;
		}

		if(_consumer.kind !== 'video') {
			return false;
		}

		if(_consumer.hasOwnProperty("appData") && _consumer.appData.source !== 'webcam') {
			return false;
		}

		const nowTime = Date.now();
		if(!this._mutedTrackCounts.hasOwnProperty(id)) {
			this._mutedTrackCounts[id] = { count: 0, timestamp: nowTime, producerPeerId: _consumer.appData.peerId };
		}

		if(!_consumer.track.onunmute) {
			_consumer.track.onunmute = (ev) =>{
				this._mutedTrackCounts[id].count = 0;
				this._mutedTrackCounts[id].timestamp = Date.now();
				this._mutedTrackCounts[id].producerPeerId = _consumer.appData.peerId;
			};
		}

		if(!_consumer.track.muted || !_consumer.track.enabled) {
			this._mutedTrackCounts[id].count = 0;
			this._mutedTrackCounts[id].timestamp = nowTime;
			this._mutedTrackCounts[id].producerPeerId = _consumer.appData.peerId;
			return false;
		}

		this._mutedTrackCounts[id].count = this._mutedTrackCounts[id].count + 1;
		if (this._mutedTrackCounts[id].count <= THREDHOLD_COUNT) {
			this._mutedTrackCounts[id].timestamp = nowTime;
			this._mutedTrackCounts[id].producerPeerId = _consumer.appData.peerId;
		}

		let compareCount = (fromActiveSpeaker ? THREDHOLD_COUNT : THREDHOLD_COUNT);
		if(_consumer.track && (_consumer.track.muted || _consumer.track.readyState == 'ended') && (this._mutedTrackCounts[id].count > compareCount && nowTime > this._mutedTrackCounts[id].timestamp + THREDHOLD_INTERVAL)) { // restartTrack, 还是需要严格限制条件

			logger.debug(`poll_log | handleTrackMuted, count=${this._mutedTrackCounts[id].count}, time=${this._mutedTrackCounts[id].timestamp}, elapse=${nowTime - this._mutedTrackCounts[id].timestamp} :${JSON.stringify(data)}, ${fromActiveSpeaker ? 'fromActiveSpeaker' : ''}`);
			
			delete this._mutedTrackCounts[id];
			_consumer.track.onunmute = null;

			this._sendRequest('restartTrack', data);

			logger.debug(`poll_log | handleTrackMuted:${JSON.stringify(data)}, ${fromActiveSpeaker ? 'fromActiveSpeaker' : ''}`);

			return true;
		}

		return false;
	}

	/**
	 * 检测并处理聚光灯下没有 consumer, 但是摄像头打开的状态
	 */
	handleSpotlightsCameraOn() {
		
		const consumerPeerId = this._peerId;
		const spotlightConsumers = Array.from(this._consumers.values());
		for (let i = 0; i < this._spotlightPeerIds.length; i++) {
			const producerPeerId = this._spotlightPeerIds[i];
			const c = spotlightConsumers.find(c => c.kind === 'video' && c.appData.peerId === producerPeerId);
			const producerPeer = store.getState().peers[producerPeerId];
			if(!producerPeer) {
				continue;
			}

			const isCameraOn = producerPeer.isCameraOn;

			logger.warn(`poll_log | (删除)handleSpotlightsCameraOn: consumer:${consumerPeerId}, producer:${producerPeerId} isCameraOn:${isCameraOn}, c is ${!c ? "null" : "not null"}`);

			if(!c && isCameraOn) { // 没有 consumer, 但是摄像头是开启的 peer
				logger.warn(`poll_log | handleSpotlightsCameraOn: consumer:${consumerPeerId}, producer:${producerPeerId}`);
				this._sendRequest('createTrack', {
					consumerPeerId,
					producerPeerId
				});
			}
		}
	}

	/**
	 * 检测并处理聚光灯下 consumer 的 track 状态
	 */
	handleSpotlightsTrackMuted() {
		const TIME_INTERVAL = 5000;
		if (this._timestampHandle === undefined) {
			this._timestampHandle = 0;
			this._timestampInterval = TIME_INTERVAL;
		}

		const nowTime = Date.now();
		let countHandled = 0;
		const spotlightConsumers = Array.from(this._consumers.values());
		for (let i = 0; i < this._spotlightPeerIds.length; i++) {
			const pId = this._spotlightPeerIds[i];
			const c = spotlightConsumers.find(c => c.kind === 'video' && c.appData.peerId === pId);
			if (!c || !c.track.muted) continue;

			if (nowTime < this._timestampHandle + this._timestampInterval) continue;

			// fromActiveSpeaker: true, 不让 handleTrackMuted() 计数
			let data = { id: c.id, producerId: c.producerId, producerPeerId: pId, fromActiveSpeaker: true };
			const result = this.handleTrackMuted(data);
			if (result) {
				this._timestampHandle = nowTime;
				countHandled++;
				this._timestampInterval = this._timestampInterval * 2;
			}
		}

		if (countHandled === 0) {
			this._timestampHandle = 0;
			this._timestampInterval = TIME_INTERVAL;
		}
	}

	/**
	 * joinType 0:邀请码入会;1:账号/密码入会;2:第三方token入会
	 *
	 * @param { joinType } param0
	 */
	async join({
		roomId,
		joinVideo,
		joinAudio,
		joinToken = '',
		roomPwd = '',
		joinType = 0
	}) {

		await this._loadDynamicImports();

		this._roomId = roomId;

		store.dispatch(roomActions.setRoomName(roomId));

		this._signalingUrl = getSignalingUrl(this.oldPeerId, this.peerId, roomId);
		this.oldPeerId = '';

		const {
			displayName,
			audioMuted,
			videoMuted
		} = store.getState().settings;

		// 登录账号的唯一Id也传给服务端
		const suid = (store.getState().me.userInfo && store.getState().me.userInfo.suid) ? store.getState().me.userInfo.suid : '';
		const _appVer = appVerInfo.version.substring(0, appVerInfo.version.length - 4);
		this._signalingUrl = `${this._signalingUrl}&suid=${suid}&displayName=${displayName}&audioMuted=${audioMuted ? '1' : '0'}&videoMuted=${videoMuted ? '1' : '0'}&joinToken=${joinToken}&roomPwd=${roomPwd}&joinType=${joinType}&sysName=${this._device.browser.getOSName()}&sysVersion=${this._device.browser.getOSVersion()}&browserKernel=${this._device.browser.getBrowserName()}&browserVersion=${this._device.browser.getBrowserVersion()}&webAppVersion=${_appVer}`;

		logger.debug(`_signalingUrl: ${this._signalingUrl}`);

		this._screenSharing = ScreenShare.create(this._device);

		this._signalingSocket = io(this._signalingUrl, {
			//transports: ['websocket']
			/*
			reconnection （布尔型）是否自动重新建立连接，默认为true
			reconnectionAttempts （Number） 尝试重连的次数，默认为无限次
			reconnectionDelay （数值型） 重新创建连接的延迟时长，默认为1000毫秒，受randomizationFactor正负加减的影响。比如默认的初始化延迟将在500至1500毫秒之间。
			reconnectionDelayMax （数值型）最大的重连等待时间，默认为5000毫秒。每一次尝试都会以两倍的增量增加重连的时间。
			randomizationFactor （数值型）默认为0.5，最小为0，最大为1.
			timeout （数值型） connect_error和connect_timeout事件触发前的延迟时间，默认为20000毫秒。
			*/
			// timeout : 1000 * 2
		});

		store.dispatch(roomActions.setRoomState('connecting'));

		this._signalingSocket.on('connect', () => {
			//this._signalingSocket.sendBuffer= [];

			logger.debug('signaling Peer "connect" event');
		});

		this._signalingSocket.on('disconnect', (reason) => {

			logger.warn('signaling Peer "disconnect" event [reason:"%s"]', reason);

			if (this._closed) {
				return;
			}

			if (reason === 'transport close' || reason === 'io server disconnect')  // io server disconnect
			//if (reason === 'io server disconnect')  // io server disconnect
			{
				store.dispatch(requestActions.notify({
					text : intl.formatMessage({
						id             : 'socket.disconnected',
						defaultMessage : 'You are disconnected'
					})
				}));

				this.close();
			}
			// 尝试重新连接
			store.dispatch(requestActions.notify({
				text : intl.formatMessage({
					id             : 'socket.reconnecting',
					defaultMessage : 'You are disconnected, attempting to reconnect'
				})
			}));

			store.dispatch(roomActions.setRoomState('connecting'));

		});

		this._signalingSocket.on('reconnect_failed', () =>
		{
			logger.warn('signaling Peer "reconnect_failed" event');

			store.dispatch(requestActions.notify(
				{
					text : intl.formatMessage({
						id             : 'socket.disconnected',
						defaultMessage : 'You are disconnected'
					})
				}));

			this.close();
		});

		this._signalingSocket.on('connect_error',() => {
			
			logger.warn('signaling Peer "connect_error" event');

			store.dispatch(requestActions.notify({
				text : intl.formatMessage({
					id             : 'socket.disconnected',
					defaultMessage : 'You are disconnected'
				})
			}));

			this.close();
		});

		this._signalingSocket.on('connect_timeout',() => {

			logger.warn('signaling Peer "connect_timeout" event');
		});		

		this._signalingSocket.on('reconnect', (attemptNumber) =>
		{
			logger.debug('signaling Peer "reconnect" event [attempts:"%s"]', attemptNumber);

			store.dispatch(requestActions.notify(
				{
					text : intl.formatMessage({
						id             : 'socket.reconnected',
						defaultMessage : 'You are reconnected'
					})
				}));

			store.dispatch(roomActions.setRoomState('connected'));
		});

		this._signalingSocket.on('request', async (request, cb) =>
		{
			logger.info('socket "request" event [method:"%s", data:"%o"]', request.method, request.data);

			switch (request.method)
			{
				case 'newConsumer':
				{
					const {
						peerId,
						producerId,
						id,
						kind,
						rtpParameters,
						type,
						appData,
						producerPaused
					} = request.data;

					if (appData.source === 'screen') {

						let oldScreenConsumer = Object.values(this._consumers).find(c => c.appData.source === 'screen');
						if(oldScreenConsumer) {
							
							await this._handleShareConsumerClosed({ consumerId: oldScreenConsumer.id, source: 'newConsumer Request'});								
						}
					}

					const _appData = { 
						...appData, 
						peerId 
					}; 

					if (kind === 'video' && appData.source !== 'screen') {

						_appData['isFirstCreated'] = true;
					}
						
					const consumer = await this._recvTransport.consume({
						id,
						producerId,
						kind,
						rtpParameters,
						appData : _appData // Trick.
					});

					// Store in the map.
					this._consumers.set(consumer.id, consumer);

					consumer.on('transportclose', () => {

						this._consumers.delete(consumer.id);
					});

					const { spatialLayers, temporalLayers } = mediasoupClient.parseScalabilityMode(consumer.rtpParameters.encodings[0].scalabilityMode);

					store.dispatch(consumerActions.addConsumer({
						id                     : consumer.id,
						peerId                 : peerId,
						kind                   : kind,
						type                   : type,
						locallyPaused          : false,
						remotelyPaused         : producerPaused,
						rtpParameters          : consumer.rtpParameters,
						source                 : consumer.appData.source,
						qlevel                 : kind === 'video' ? -1 : null,
						spatialLayers          : spatialLayers,
						temporalLayers         : temporalLayers,
						preferredSpatialLayer  : spatialLayers - 1,
						preferredTemporalLayer : temporalLayers - 1,
						priority               : 1,
						codec                  : consumer.rtpParameters.codecs[0].mimeType.split('/')[1],
						track                  : consumer.track
					}, peerId));

					await this._resumeConsumer(consumer);
					
					if (appData.source === 'screen') {
					  
						this.setConsumerPreferedLayers(consumer.id, 0, 0, 'request -> newConsumer');
					}
					
					cb(null);

					if (kind === 'audio')
					{
						consumer.volume = 0;

						const stream = new MediaStream();
						stream.addTrack(consumer.track);

						if (!stream.getAudioTracks()[0]) {

							throw new Error('request.newConsumer | given stream has no audio track');
						}

						consumer.hark = hark(stream, { play: false });
						consumer.hark.on('volume_change', (volume) => {

							volume = Math.round(volume);
							if (consumer && volume !== consumer.volume) {

								consumer.volume = volume;

								store.dispatch(peerVolumeActions.setPeerVolume(peerId, volume));
							}
						});
					} else if (kind === 'video') {
						// 如果收到共享视频(主席同步模式可以发起共享)
						if (appData.source === 'screen') {

							// 标记会议室有共享视频
							store.dispatch(roomActions.setScreenSharing(true));

							const _historyMode = store.getState().room.historyMode;
							_historyMode.push(store.getState().room.mode)

							// 如果为会见共享,则判断 isDuoVideoPause
							if (appData.createdBy && appData.createdBy === 'HJ') {
								const _config = store.getState().room.config;
								if (_config) {
									// 会控暂停/取消暂停共享
									if (_config.isDuoVideoPause) {

										store.dispatch(consumerActions.setConsumerPaused(consumer.id, 'remote'));
									}
									else {
										store.dispatch(consumerActions.setConsumerResumed(consumer.id, 'remote'));
									}
								}
							}
							const _mode = 'speaker';
							await this._spotlights.handleShare(peerId, _mode);
						}

						// 当前新窗口中视频属性
						const { 
							windowPeerId, 
							windowConsumerId, 
							windowVideoSource 
						} = store.getState().room;

						if (windowPeerId === peerId && windowVideoSource === appData.source &&  windowConsumerId !== consumer.id) {

							store.dispatch(roomActions.setWindowConsumerId(consumer.id));
						}
					}

					break;
				}
				default:
				{
					logger.error('unknown request.method "%s"', request.method);

					cb(500, `unknown request.method "${request.method}"`);
				}
			}
		});

		this._signalingSocket.on('notification', async (notification) =>
		{
			if (!['consumerScore', 'activeSpeaker', 'producerScore'].includes(notification.method)) {

				logger.info('socket "notification" event [method:"%s", data:"%o"]', notification.method, notification.data);
			}

			/**
			 * 设置联席主持人
			 */
			const setCohosts = (cohostSuids, isCohost, init = false) => {

				// 在此处设置联席主持人
				const { chairPeerId } = store.getState().room;

				if (init) {

					store.dispatch(roomActions.setCohostSuids(cohostSuids));
				} else if (Array.isArray(cohostSuids)) {

					for (const suid of cohostSuids) {

						if (isCohost) {

							store.dispatch(roomActions.addCohostSuid(suid));
						} else {

							store.dispatch(roomActions.removeCohostSuid(suid));
						}
					}
				}

				const _peerList = store.getState().peers ? Object.values(store.getState().peers) : [];

				logger.debug(`setCohosts -> cohostSuids:[${JSON.stringify(cohostSuids)}] _peerList:[${JSON.stringify(_peerList)}]`);

				if (Array.isArray(cohostSuids) && _peerList.length > 0) {

					for (const suid of cohostSuids) {

						const _peer = _peerList.find((v) => v.suid == suid);
						if (_peer) {

							logger.debug(`setCohosts -> cohostSuids:[${JSON.stringify(cohostSuids)}] _peer:[${JSON.stringify(_peer)}] chairPeerId:[${chairPeerId}]`);

							store.dispatch(peerActions.setPeerCohost(_peer.id, isCohost));

							logger.debug(`setCohosts -> _peer.id:[${_peer.id}] isCohost:[${isCohost}]`);

							// 不允许将主持人设置为联席主持人
							if (_peer.id !== chairPeerId) {

								// 如果自己成为联席主持人,需要 toast 提示
								if (_peer.id === this.peerId) {

									// 收回联席主持人权限时关掉所有操作对话框,并给出提示
									const {
										auditRaiseHandOpen,
										forceMuteAllOpen,
										isForceMuteAll,
										recordConfirmOpen,
										subtitleState, 
										subtitleOpen
									} = store.getState().room;


									if (isCohost) {

										const { 
											applyUnMuted, 
											allowUnMuted,
											forceMuted
										} = store.getState().me;

										if (applyUnMuted && !allowUnMuted) {
											// 取消申请(await)
											this.requestRaiseHand(false);
										}

										if (forceMuted && !isForceMuteAll) {

											store.dispatch(roomActions.setIsForceMuteAll(true));

										} else if (!forceMuted && isForceMuteAll) {

											store.dispatch(roomActions.setIsForceMuteAll(false));
										}

										const { 
											hasRecording,
											meIsRecording
										} = store.getState().room;

										if (hasRecording && !meIsRecording) {

											store.dispatch(roomActions.setMeIsRecording(true));
										}

										store.dispatch(requestActions.notify({
											text : intl.formatMessage({
												id             : 'message.upgradeCohost',
												defaultMessage : 'You have become a co host'
											})
										}))
									} else {

										if (isForceMuteAll) {

											store.dispatch(roomActions.setIsForceMuteAll(false));
										}

										if (recordConfirmOpen) {

											store.dispatch(roomActions.setRecordConfirmOpen(false));
										}

										if (forceMuteAllOpen) {

											store.dispatch(roomActions.setForceMuteAllOpen(false));
										}

										const {
											audioMuted
										} = store.getState().settings;

										if (!audioMuted) {

											store.dispatch(meActions.setAllowUnMuted(true));
										}

										if (subtitleState === 1 && subtitleOpen) {

											store.dispatch(roomActions.setSubtitleOpen(false));
										}

										store.dispatch(requestActions.notify({
											text : intl.formatMessage({
												id             : 'message.cancelCohost',
												defaultMessage : 'Host reclaims your co host privileges'
											})
										}));
									}
								}
							}
						}
					}
				}
			}

			try
			{
				switch (notification.method)
				{
					case 'newConsumer': {
						const {
							peerId,
							producerId,
							id,
							kind,
							rtpParameters,
							type,
							appData,
							producerPaused
						} = notification.data;

						let consumer = this._consumers.get(id);
						if(consumer) {
							await this._recvTransport.replaceConsume({
								id,
								producerId,
								appData
							});

							if (consumer.paused) {
								consumer.resume();
							}
							if (!consumer.track.enabled) {
								consumer.track.enabled = true;
							}
						} else {

							const _appData = { 
								...appData, 
								peerId 
							};

							if (kind === 'video' && appData.source !== 'screen') {
								
								_appData['isFirstCreated'] = true;
							}

							consumer = await this._recvTransport.consume({
								id,
								producerId,
								kind,
								rtpParameters,
								appData: _appData // Trick.
							});
						}

						// Store in the map.
						this._consumers.set(consumer.id, consumer);

						consumer.on('transportclose', () => {

							this._consumers.delete(consumer.id);
						});

						const { spatialLayers, temporalLayers } = mediasoupClient.parseScalabilityMode(consumer.rtpParameters.encodings[0].scalabilityMode);

						store.dispatch(consumerActions.addConsumer({
							id: consumer.id,
							peerId: peerId,
							kind: kind,
							type: type,
							locallyPaused: false,
							remotelyPaused: producerPaused,
							rtpParameters: consumer.rtpParameters,
							source: consumer.appData.source,
							qlevel: kind === 'video' ? -1 : null,
							spatialLayers: spatialLayers,
							temporalLayers: temporalLayers,
							preferredSpatialLayer: spatialLayers - 1,
							preferredTemporalLayer: temporalLayers - 1,
							priority: 1,
							codec: consumer.rtpParameters.codecs[0].mimeType.split('/')[1],
							track: consumer.track
						}, peerId));
						await this._resumeConsumer(consumer);

						if (appData.source === 'screen') {
							
							this.setConsumerPreferedLayers(consumer.id, 0, 0, 'notification->newConsumer');
						}

						if (kind === 'audio') {
							
							consumer.volume = 0;

							const stream = new MediaStream();
							stream.addTrack(consumer.track);

							if (!stream.getAudioTracks()[0]) {

								throw new Error('request.newConsumer | given stream has no audio track');
							}

							consumer.hark = hark(stream, { play: false });
							consumer.hark.on('volume_change', (volume) => {

								volume = Math.round(volume);
								if (consumer && volume !== consumer.volume) {

									consumer.volume = volume;

									store.dispatch(peerVolumeActions.setPeerVolume(peerId, volume));
								}
							});
						} else if (kind === 'video') {
							// 如果收到共享视频(主席同步模式可以发起共享)
							if (appData.source === 'screen') {

								// 标记会议室有共享视频
								store.dispatch(roomActions.setScreenSharing(true));

								const _historyMode = store.getState().room.historyMode;
								_historyMode.push(store.getState().room.mode)

								// 如果为会见共享,则判断 isDuoVideoPause
								if (appData.createdBy && appData.createdBy === 'HJ') {
									const _config = store.getState().room.config;
									if (_config) {
										// 会控暂停/取消暂停共享
										if (_config.isDuoVideoPause) {

											store.dispatch(consumerActions.setConsumerPaused(consumer.id, 'remote'));
										}
										else {
											store.dispatch(consumerActions.setConsumerResumed(consumer.id, 'remote'));
										}
									}
								}

								const _mode = 'speaker';
								await this._spotlights.handleShare(peerId, _mode);
							}

							// 当前新窗口中视频属性
							const { 
								windowPeerId, 
								windowConsumerId, 
								windowVideoSource 
							} = store.getState().room;

							if (windowPeerId === peerId && windowVideoSource === appData.source && windowConsumerId !== consumer.id) {

								store.dispatch(roomActions.setWindowConsumerId(consumer.id));
							}
						}

						break;
					}
					case 'replaceConsumer':
					{
						const {
							id,
							producerId,
							prevPeerId,
							producerPeerId,  // 生产者 peerId
							qlevel,
							appData
						} = notification.data;

						const _consumer = this._consumers.get(id);

						if (_consumer) {

							await this._recvTransport.replaceConsume({
								id,
								producerId,
								appData
							});
							if (_consumer.track && !_consumer.track.enabled) {
								_consumer.track.enabled = true;
							}

							await this._resumeConsumer(_consumer);
							const _SpatialLayer = QLevelMapSpatialLayer.get(qlevel);
							
							this.setConsumerPreferedLayers(_consumer.id, _SpatialLayer, 0, 'replaceConsumer');

							//#region 新增代码
							if (_consumer.paused) {
								
								_consumer.resume();
							}
							//#endregion

							store.dispatch(consumerActions.setConsumerPeerIdQlevel(id, producerPeerId, qlevel));

							// 将旧 producerPeerId 对象中的 consumers 迁移到新 producerPeerId 对象中 replaceConsume
							store.dispatch(peerActions.replacePeerConsume(prevPeerId, producerPeerId, id));

							// 当前新窗口中视频属性
							const { 
								windowPeerId, 
								windowConsumerId, 
								windowVideoSource 
							} = store.getState().room;

							if (windowPeerId === appData.peerId && windowVideoSource === appData.source && windowConsumerId !== _consumer.id) {

								store.dispatch(roomActions.setWindowConsumerId(_consumer.id));
							}
						}

						// 新增代码
						this.handleTrackMuted(notification.data); // 处理 track 异常情况

						break;
					}
					case 'changePeerId':  // 同步会见peerId
					{
						const { peerId, hjPeerId } = notification.data;
						logger.debug('changePeerId ', notification.data);

						if (this.peerId === peerId) {

							this.peerId = hjPeerId;

							store.dispatch(peerVolumeActions.removePeerVolume(peerId));
							store.dispatch(peerVolumeActions.setPeerVolume(hjPeerId, -100));
							store.dispatch(meActions.setMyPeerId(hjPeerId));
						}

						break;
					}
					case 'enteredLobby':
					{
						store.dispatch(roomActions.setInLobby(true));

						const { displayName } = store.getState().settings;
						const { picture } = store.getState().me;

						await this.sendRequest('changeDisplayName', { displayName });
						await this.sendRequest('changePicture', { picture });
						break;
					}
					case 'signInRequired':
					{
						store.dispatch(roomActions.setSignInRequired(true));

						break;
					}
					case 'joinHJFail':
					{
						logger.debug('收到远端入会失败通知', notification.data);

						const { error } = notification.data;

						if (error === 4608) {
							// sfugw 不存在
						}

						store.dispatch(roomActions.setJoinError({
							hasError: true,
							errorCode: notification.data.error,   // 0xf0001 主持人开启呼叫等待,加入会议失败
						}));

						break;
					}
					case 'joinStatus':
					{
						logger.debug(`收到入会状态通知`, notification.data);

						store.dispatch(roomActions.setJoining(false));

						break;
					}
					case 'confStatus':  // 当前会议室状态
					{
						const {

							confId,
							peerId,
							confName,
							confMode,              // 0：自由模式，1：演讲模式
							isDuoVideoStart,       // 共享状态
							isConfLocked,          // 会议锁定状态
							isForceMute,           // 强制静音状态
							isRecord,              // 当前会议是否正在录制
							isDisplayTername,      // 显示名称状态
							isSubtitleStart,       // 字幕显示状态
							isFilterNoVideo,       // 主席模式过滤无图像视频状态
							isDuoVideoPause,       // 共享暂停状态
							
							opType,                // 开启/禁用实时字幕状态
							chairPeerId,           // 当前会议室主持人peerId

							cohostPeerIds,         // 在线联席主持人 peerId 数组
							cohostSuids            // 所有联席主持人(在线以及非在线) suid 数组

						} = notification.data;

						this.roomName = confName;

						store.dispatch(roomActions.setConfig({
							confId, 
							peerId, 
							confName, 
							confMode,
							isDuoVideoStart, 
							isConfLocked, 
							isForceMute, 
							isDisplayTername,
							isSubtitleStart, 
							isFilterNoVideo, 
							isDuoVideoPause, 
							opType
						}));

						// 设置联席主持人
						setCohosts(cohostSuids, true, true);

						if (confMode === 0) {

							store.dispatch(roomActions.setFilterVideo(false));
						} else {

							store.dispatch(roomActions.setFilterVideo(isFilterNoVideo));
						}
						store.dispatch(meActions.setForceMuted(isForceMute));

						const _mode = confMode === 1 ? true : false;
						store.dispatch(roomActions.setForceMode(_mode));

						// 如果当前会议正在录制,需要同步状态
						store.dispatch(roomActions.setHasRecording(isRecord));

						const _meIsModerator = meIsModeratorSelector(store.getState());
						const _meIsCohost = meIsCohostSelector(store.getState());
						if (_meIsModerator || _meIsCohost) {

							store.dispatch(roomActions.setMeIsRecording(isRecord));
						} else {

							store.dispatch(roomActions.setHasRecordingPrompt(isRecord));
						}

						// 设置会议室主持人
						if (chairPeerId && !chairPeerId.endsWith('-0')) {

							const { 
								chairPeerId : _chairPeerId 
							} = store.getState().room;
							
							if (!_chairPeerId) {

								store.dispatch(roomActions.setChairPeerId(chairPeerId));
							}
						}

						break;
					}
					case 'cohostsChanged': {  // 收到设置联席主持人的通知
						const {
							isOk,
							errorCode,
							failReason,
							peerId,
							opType,
							cohostSuids            // 所有联席主持人(在线以及非在线) suid 数组
						} = notification.data;

						if (isOk) {
							if ([1, 2].includes(opType) && Array.isArray(cohostSuids)) {

								const _isCohost = opType === 1 ? true : false;

								// 设置联席主持人(取消/设置)
								setCohosts(cohostSuids, _isCohost);
							}
						} else {

							const _meIsModerator = meIsModeratorSelector(store.getState());

							if (_meIsModerator) {

								// 如果出错则需要把出错信息通知给主持人
								const _lang = store.getState().intl.lang;
								const _errMsg = getErrMesage(errorCode, _lang, false);
								// 显示错误提示
								store.dispatch(requestActions.notify({
									text: _errMsg
								}));
							}
						}

						break;
					}
					case 'confInfo':  // 当前会议室信息
					{
						const {
							thirdConfId,
							confName,
							confId,
							reportId,
							domain,
							suid,

							signEnable,            // 是否开启签到 "0" / "1"
							voteEnable,            // 是否开启投票 "0" / "1"
							watermarkEnable,       // 是否开启水印 "0" / "1"

							// subtitleEnable,        // 1 : 关闭; 2 : 开启
							// recordingPromptEnable  // 0 / 1
						} = notification.data;

						this.roomId = thirdConfId;
						this.roomName = confName;

						const _userInfo = store.getState().me.userInfo;

						// 更新 userInfo 中的内容, 添加 confId reportId domain ( 新平台 api 支持)
						store.dispatch(meActions.setUserInfo({
							..._userInfo, 
							confId, 
							reportId, 
							domain, 
							suid
						}));

						store.dispatch(roomActions.setVoteEnable(voteEnable));

						// store.dispatch(roomActions.setSubtitleEnable(subtitleEnable));
						// store.dispatch(roomActions.setRecordingPromptEnable(Boolean(recordingPromptEnable)));

						break;
					}
					case 'watermark':
					{

						const generateWatermark = () => {

							let _watermark = window.sessionStorage['watermark'];

						    logger.debug(`roomWatermark: ${_watermark}`);

							if (_watermark) {
								try {

									_watermark = JSON.parse(_watermark);
								}
								catch(ex) {
									logger.warn(`转换 watermark 出错: ${ex.message}`);
								}
							}

							if (_watermark && _watermark.isEnable === '1' && Array.isArray(_watermark.columns)) {

								const _userInfo = store.getState().me.userInfo;

								const _nickname = _userInfo.nickname ? _userInfo.nickname : store.getState().settings.displayName;

								const _columnsMaps = {

									user_nickname   : _nickname,             //  姓名
									user_phone      : _userInfo.phone,       //  手机号
									conf_name       : this.roomName,         //  会议主题
									custom          : _watermark.custom,     //  自定义
									user_account    : _userInfo.account,     //  帐号
									user_email      : _userInfo.email,       //  邮箱
									conf_subject    : this.roomId,           //  会议号
									current_date    : formatDate(Date.now()) //  日期
								}

								const _watermarkValue = [];
								for (const column of _watermark.columns) {

									if (_columnsMaps[column]) {

										_watermarkValue.push(_columnsMaps[column]);
									}
								}

								logger.debug(`水印内容为: ${_watermarkValue.join(';')}`);

								// 在这里获取水印数据
								watermark.load({ text: _watermarkValue.join(';') });
							}
						};

						const formatDate = (date) => {

							let _date = new Date(date),
							  _month = '' + (_date.getMonth() + 1),
							  _day = '' + _date.getDate(),
							  _year = _date.getFullYear();

							if (_month.length < 2) _month = '0' + _month;

							if (_day.length < 2) _day = '0' + _day;

							return [_year, _month, _day].join('-');
						};

						const {
							isEnable,   // '0' / '1'
							columns
						} = notification.data;

						const _watermark = window.sessionStorage['watermark'];
						if (!_watermark) {

							window.sessionStorage['watermark'] = JSON.stringify(notification.data);
						}
						// 将水印转为字符串保存至状态变量中(是否可以不需要)
						//store.dispatch(roomActions.setWatermark(notification.data));

						generateWatermark();

						this._watermarkTimer = setInterval(() => {

							generateWatermark();

						}, 60 * 60 * 1000);


						break;
					}
					case 'shareStatus':   // 是否有共享视频
					{
						const {
							sharepeerId,
							share,
							peerId,
							isduovideopause : isDuoVideoPause   // 是否暂停
						} = notification.data;

						let _config = store.getState().room.config;
						if (_config) {

							_config = {
								..._config, 
								isDuoVideoStart: share, 
								isDuoVideoPause 
							};
							store.dispatch(roomActions.setConfig(_config));

							if (isDuoVideoPause) {

								const _consumers = store.getState().consumers;
								const _shareConsumer = Object.values(_consumers).find((consumer) => consumer.source === 'screen');
								if (_shareConsumer) {

									store.dispatch(consumerActions.setConsumerPaused(_shareConsumer.id, 'remote'));
								}
							}
						}
						// 会控停止了此终端的共享
						if (!share && this._peerId === sharepeerId && sharepeerId === peerId) {

							this.disableScreenSharing();
						}

						break;
					}
					case 'applyShare': // 自己在共享时，收到对方的共享请求
					{
						const { peerId } = notification.data;

						if (this._screenSharingProducer) {
							// 处理是否接收请求
							this._applySharePeerId = peerId;
							store.dispatch(roomActions.setShareDialogOpen(true));
						}

						break;
					}
					case 'replyApplyShare': // 其它 webapp 终端共享时，自己请求共享收到回复
					{
						const { isok } = notification.data;

						if (isok) {
							// 同意共享
							store.dispatch(requestActions.notify({
								text : intl.formatMessage({
									id             : 'room.screenSharingAgreed',
									defaultMessage : 'The other party has agreed to your sharing request'
								})
							}));
							this.updateScreenSharing({ start: true }); // 打开自己的共享请求
						} else {
							// 拒绝共享
							store.dispatch(requestActions.notify({
								text : intl.formatMessage({
									id             : 'room.screenSharingRefused',
									defaultMessage : 'Your sharing request was rejected'
								})
							}));
						}

						break;
					}
					case 'webAppShareStatus': // 会见共享时, 自己请求共享收到会见回复,与 replyApplyShare 对应
					{
						const { isok } = notification.data;

						// isok true 开始共享; false 共享请求被拒绝
						if (!isok) {

							this.disableScreenSharing();

							store.dispatch(requestActions.notify({
								text : intl.formatMessage({
									id             : 'room.screenSharingRefused',
									defaultMessage : 'Your sharing request was rejected'
								})
							}));
						}

						break;
					}
					case 'pauseShare':  // 共享视频暂停/取消暂停
					{
						logger.debug('收到共享暂停通知', notification.data);
						const { pause } = notification.data;

						let _config = store.getState().room.config;
						if (_config) {

							_config = { 
								..._config, 
								isDuoVideoPause: pause 
							};
							store.dispatch(roomActions.setConfig(_config));
						}

						const _consumers = store.getState().consumers;

						const _shareConsumer = Object.values(_consumers).find((consumer) => consumer.source === 'screen');
						if (_shareConsumer) {
							// 会控暂停/取消暂停共享
							if (pause) {

								store.dispatch(consumerActions.setConsumerPaused(_shareConsumer.id, 'remote'));
							}
							else {

								store.dispatch(consumerActions.setConsumerResumed(_shareConsumer.id, 'remote'));
							}
						}

						break;
					}
					case 'producerIsConsumed':  // 收到视频有没被选看事件
					{
						const { producerId, isConsumed, source, qlevel } = notification.data;

						const _producer = store.getState().producers[producerId];

						if (_producer) {

							const { isConsumed: isConsumedOld, qlevel: qlevelOld } = _producer;
							// 目前只判断主视频流
							if (this._webcamProducer
								&& this._webcamProducer.id === producerId
								&& (isConsumed !== isConsumedOld || qlevel !== qlevelOld)) {

								//logger.debug(`终端${this._peerId} 视频流 ${isConsumed ? '' : '没有'}被选看 来源:${source}`);

								if (isConsumed) {

									if (this._webcamProducer.paused) {

										this._webcamProducer.resume();
									}

									if (this._isFirstRunSetMaxQLevel) {

										logger.debug(`producerIsConsumed setMaxSpatialLayer: ${qlevel} 延后执行选流`);

										this._recvMaxQLevel = qlevel;

										if (this._webcamProducerCreated) {
											const _maxSpatialLayer = QLevelMapSpatialLayer.get(qlevel);

											if ([0, 1, 2].includes(_maxSpatialLayer)) {

												logger.debug(`producerIsConsumed -> setMaxSpatialLayer: ${_maxSpatialLayer}`);
												await this._webcamProducer.setMaxSpatialLayer(_maxSpatialLayer);
											}

											this._isFirstRunSetMaxQLevel = false;
										}
									} else {

										const _maxSpatialLayer = QLevelMapSpatialLayer.get(qlevel);

										if ([0, 1, 2].includes(_maxSpatialLayer)) {

											logger.debug(`producerIsConsumed -> setMaxSpatialLayer: ${_maxSpatialLayer}`);
											await this._webcamProducer.setMaxSpatialLayer(_maxSpatialLayer);
										}
									}

								} else if (!this._webcamProducer.paused) {

									this._webcamProducer.pause();
								}

								logger.debug(`终端${this._peerId} 视频流 ${isConsumed ? '' : '没有'}被选看 来源:${source}`);

								store.dispatch(producerActions.isConsumed(producerId, isConsumed));

								const _qlevel = isConsumed ? qlevel : 0;
								store.dispatch(producerActions.setQLevel(producerId, _qlevel));

							}
						} else {

							logger.debug(`producerId: ${producerId} 不存在`);
						}

						break;
					}

					case 'chairChange':  // 通知谁是主持人
					{
						const { chairPeerId } = notification.data;
						
						const { 
							chairPeerId : prevChairPeerId 
						} = store.getState().room;

						logger.debug(`chairChange: ${chairPeerId} prevChairPeerId: ${prevChairPeerId} peerId:${this.peerId}`);

						if (this.peerId === prevChairPeerId && prevChairPeerId !== chairPeerId) {
							// 卸任方,则关掉所有操作对话框,并提示终端主持人权限已转移
							const {
								auditRaiseHandOpen,
								forceMuteAllOpen,
								isForceMuteAll
							} = store.getState().room;
							// 举手审核框打开
							if (auditRaiseHandOpen) {

								store.dispatch(roomActions.setAuditRaiseHandOpen(false));
							}
							// 强制静音确认框打开
							if (forceMuteAllOpen) {

								store.dispatch(roomActions.setForceMuteAllOpen(false));
							}
							if (isForceMuteAll) {

								store.dispatch(roomActions.setIsForceMuteAll(false));
								// 将本地音频设备关掉
								//await this.muteMic()
								const { forceMuted } = store.getState().me;
								if (!forceMuted) {

									store.dispatch(meActions.setForceMuted(true));
								}
							}

							store.dispatch(requestActions.notify({
								text : intl.formatMessage({
									id             : 'label.chairTransferred',
									defaultMessage : 'Host authority has been transferred'
								})
							}))

						} else if (this.peerId === chairPeerId) {
							// 上任方
							if (prevChairPeerId !== chairPeerId) {

								store.dispatch(requestActions.notify({
									text : intl.formatMessage({
										id             : 'message.upgradeChair',
										defaultMessage : 'Upgrade as chair'
									})
								}))
							}
							// 如果当前会议是强制静音状态,是否自己举手了
							const { raisedHand, forceMuted } = store.getState().me;

							if (forceMuted) {

								const { isForceMuteAll } = store.getState().room;

								if (!isForceMuteAll) {

									store.dispatch(roomActions.setIsForceMuteAll(true));
								}

								store.dispatch(meActions.setForceMuted(false));
							}

							if (raisedHand) {
								// 则需要放下手(被动放弃)
								this.requestRaiseHand(false, 1);
							}
							
							// 如果当前会中正在录制,则录制按钮的状态需要改变
							const { 
								hasRecording,
								meIsRecording 
							} = store.getState().room;

							if (hasRecording && !meIsRecording) {

								store.dispatch(roomActions.setMeIsRecording(true));
							}
						}

						// 设置会议室主持人
						store.dispatch(roomActions.setChairPeerId(chairPeerId));

						if (this.peerId !== chairPeerId) {

							const { subtitleState, subtitleOpen } = store.getState().room;

							if (subtitleState === 1 && subtitleOpen) {

								store.dispatch(roomActions.setSubtitleOpen(false));
							}
						}

						break;
					}

					case 'peerAudioMuted':
					{
						const { peerId, muted } = notification.data;
						store.dispatch(peerActions.setPeerAudioMute(peerId, muted));

						const {
							chairAudioMuteInProgress
						} = store.getState().me;

						if (chairAudioMuteInProgress) {

							store.dispatch(meActions.setChairAudioMuteInProgress(false));
						}

						break;
					}

					case 'peerVideoMuted':
					{
						const { peerId, muted } = notification.data;

						await this._spotlights.updatePeerVideoMute(peerId, muted, 'peerVideoMuted');

						const {
							chairVideoMuteInProgress
						} = store.getState().me;

						if (chairVideoMuteInProgress) {

							store.dispatch(meActions.setChairVideoMuteInProgress(false));
						}

						break;
					}

					case 'overRoomLimit':
					{
						const { maxUsersPerRoom } = notification.data;

						store.dispatch(roomActions.setMaxUsersPerRoom(maxUsersPerRoom));
						store.dispatch(roomActions.setOverRoomLimit(true));

						break;
					}

					case 'roomReady':
					{
						//this._queue.push(async () => {

							// 通知第三方应用，用户入会
							if (window.parent) {

								const _iframe = window.parent.document.getElementsByName(store.getState().room.windowOpenName);
								if (_iframe.length > 0 && _iframe[0].tagName.toLowerCase() === 'iframe') {

									window.parent.postMessage({ 
										'cmd': 'hj_user_join', 
										'msg': null 
									}, '*');
								}
							}

							const { 
								turnServers, 
								joinAutoMuteVideo, 
								joinAutoMuteAudio 
							} = notification.data;

							this._turnServers = turnServers;

							store.dispatch(roomActions.setJoined(true));  // joined = true
							store.dispatch(roomActions.setInLobby(false));

							let _joinVideo = joinVideo;
							let _joinAudio = joinAudio;
							if (joinAutoMuteVideo) {

								_joinVideo = false;
							};
							if (joinAutoMuteAudio) {

								_joinAudio = false;
							};
							
							await this._joinRoom({ 
								joinVideo: _joinVideo, 
								joinAudio: _joinAudio 
							});

						//}).catch((err) => {
						//	logger.error('处理事件 roomReady失败 ["%s"]', err.message);
						//});

						break;
					}

					case 'roomBack':
					{
						await this._joinRoom({ joinVideo, joinAudio });

						break;
					}

					case 'lockRoom':
					{
						store.dispatch(roomActions.setRoomLocked());

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.locked',
									defaultMessage : 'Room is now locked'
								})
							}));

						break;
					}

					case 'unlockRoom':
					{
						store.dispatch(roomActions.setRoomUnLocked());

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.unlocked',
									defaultMessage : 'Room is now unlocked'
								})
							}));

						break;
					}

					case 'parkedPeer':
					{
						const { peerId } = notification.data;

						store.dispatch(lobbyPeerActions.addLobbyPeer(peerId));
						store.dispatch(roomActions.setToolbarsVisible(true));

						this._soundNotification();

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.newLobbyPeer',
									defaultMessage : 'New participant entered the lobby'
								})
							}));

						break;
					}

					case 'parkedPeers':
					{
						const { lobbyPeers } = notification.data;

						if (lobbyPeers.length > 0)
						{
							lobbyPeers.forEach((peer) =>
							{
								store.dispatch(lobbyPeerActions.addLobbyPeer(peer.id));

								store.dispatch(
									lobbyPeerActions.setLobbyPeerDisplayName(
										peer.displayName,
										peer.id
									)
								);

								store.dispatch(
									lobbyPeerActions.setLobbyPeerPicture(
										peer.picture,
										peer.id
									)
								);
							});

							store.dispatch(roomActions.setToolbarsVisible(true));

							this._soundNotification();

							store.dispatch(requestActions.notify(
								{
									text : intl.formatMessage({
										id             : 'room.newLobbyPeer',
										defaultMessage : 'New participant entered the lobby'
									})
								}));
						}

						break;
					}

					case 'lobby:peerClosed':
					{
						const { peerId } = notification.data;

						store.dispatch(lobbyPeerActions.removeLobbyPeer(peerId));

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.lobbyPeerLeft',
									defaultMessage : 'Participant in lobby left'
								})
							}));

						break;
					}

					case 'lobby:promotedPeer':  // 提升
					{
						const { peerId } = notification.data;

						store.dispatch(lobbyPeerActions.removeLobbyPeer(peerId));

						break;
					}

					case 'lobby:changeDisplayName':
					{
						const { peerId, displayName } = notification.data;

						store.dispatch(lobbyPeerActions.setLobbyPeerDisplayName(displayName, peerId));

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.lobbyPeerChangedDisplayName',
									defaultMessage : 'Participant in lobby changed name to {displayName}'
								}, {
									displayName
								})
							}));

						break;
					}

					case 'lobby:changePicture':
					{
						const { peerId, picture } = notification.data;

						store.dispatch(lobbyPeerActions.setLobbyPeerPicture(picture, peerId));

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.lobbyPeerChangedPicture',
									defaultMessage : 'Participant in lobby changed picture'
								})
							}));

						break;
					}

					case 'setAccessCode':
					{
						const { accessCode } = notification.data;

						store.dispatch(roomActions.setAccessCode(accessCode));

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.setAccessCode',
									defaultMessage : 'Access code for room updated'
								})
							}));

						break;
					}

					case 'setJoinByAccessCode':
					{
						const { joinByAccessCode } = notification.data;

						store.dispatch(roomActions.setJoinByAccessCode(joinByAccessCode));

						if (joinByAccessCode)
						{
							store.dispatch(requestActions.notify(
								{
									text : intl.formatMessage({
										id             : 'room.accessCodeOn',
										defaultMessage : 'Access code for room is now activated'
									})
								}));
						}
						else
						{
							store.dispatch(requestActions.notify(
								{
									text : intl.formatMessage({
										id             : 'room.accessCodeOff',
										defaultMessage : 'Access code for room is now deactivated'
									})
								}));
						}

						break;
					}

					case 'activeSpeaker':
					{
						const { peerId } = notification.data;
						const { activeSpeakerId } = store.getState().room;

						const _activePeer = store.getState().peers[peerId];

						if (this._spotlights.allLitePeers.findIndex((v) => v.id === peerId) >= 0 && (_activePeer && _activePeer.audioMuted === false)) {

							if (this._spotlights.prevActiveId !== peerId) {

								this._spotlights.prevActiveId = peerId;  // 记录语音激励的 peerId 备用
							}

							if (activeSpeakerId !== peerId) {

								if (peerId && peerId !== this._peerId) {

									logger.debug('handleActiveSpeaker() [当前: _activePeer:%s; 历史: %s; 自已: %s] - 收到通知', JSON.stringify(_activePeer), activeSpeakerId, this._peerId);

									store.dispatch(roomActions.setRoomActiveSpeakerId(peerId));
									await this._spotlights.handleActiveSpeaker(peerId);
								}
							}
						}
						else {

							// logger.debug(`语音激励对象 ${peerId} 不在参会人列表中`);
						}
						this.handleSpotlightsTrackMuted();
						/*
						if(window.config.rejoinVideoRecovery) {
							this.handleSpotlightsCameraOn();
						}
						*/						

						break;
					}

					// 设置焦点视频
					case 'reportLockPeer':
					{
						logger.debug(`${notification.method} layoutConfig: ${JSON.stringify(this._spotlights.layoutConfig)} -> ${Date.now()}`);

						const { peerId } = notification.data;

						if (peerId) {

							store.dispatch(roomActions.setLockPeerId(peerId));

							await this._spotlights.handleFocus(peerId);
						}

						break;
					}

					// 取消焦点视频
					case 'reportUnlockPeer':
					{
						const { peerId } = notification.data;

						if (peerId) {

							store.dispatch(roomActions.setLockPeerId(null));
							await this._spotlights.handleFocus(null);
						}

						break;
					}

					// 主席模式下会议布局切换通知
					case 'layoutChange':
					{

						const { layoutMode, screenPaneList, confPollList, chairFollow } = notification.data;

						logger.debug('layoutChange [layoutMode:%s screenPaneList:%s confPollList:%s chairFollow:%s] -> %s', layoutMode, JSON.stringify(screenPaneList), JSON.stringify(confPollList), chairFollow, Date.now());

						this._pollCount++;
						logger.debug(`poll_log | layoutChange Count:${this._pollCount}`);

						if(window.config.waitForVideoWhilePolling) {

							if(this._pollCount !== 1 && this.hasTrackMuted()) {

								break;
							}
						}

						// 初始化演讲者模式下的数据
						await this._spotlights.handleLayoutChange(layoutMode, screenPaneList, confPollList);

						break;
					}

					case 'modeChange': // 主席/自由模式切换开关
					{

						const { mode } = notification.data;
						logger.debug('modeChange [mode:%s]', mode);
						const _mode = mode === 1 ? true : false;
						store.dispatch(roomActions.setForceMode(_mode));

						if (mode === 0) { // 如果收到自由模式通知,则恢复以前的模式

							store.dispatch(roomActions.setFilterVideo(false));

							const _historyMode = store.getState().room.historyMode;
							const _prevMode = _historyMode.pop();

							if (_prevMode) {

								const _layoutConfig = clone(this.viewsLayout[_prevMode]);

								await this.initLayoutConfig(_layoutConfig, _prevMode);
							}
						} else if (mode === 1) {  // 主席模式下

							// 清理自由模式设定的定时器
							clearInterval(this._spotlights.pollTimer);

							const _remoteFilterVideo  = store.getState().room.remoteFilterVideo;
							store.dispatch(roomActions.setFilterVideo(_remoteFilterVideo));

							const _historyMode = store.getState().room.historyMode;
							_historyMode.push(store.getState().room.mode);

							store.dispatch(roomActions.setLayoutOpen(false));
						}

						break;
					}

					case 'filterNoVideo': // 主持人控制过滤无图像视频通知
					{

						const { isFilter } = notification.data;
						logger.debug('主持人控制过滤无图像视频通知', isFilter);

						store.dispatch(roomActions.setRemoteFilterVideo(isFilter));
						store.dispatch(roomActions.setFilterVideo(isFilter));

						break;
					}

					case 'sameTerminal':
					{
						const { errorcode } = notification.data;

						const storage = window.sessionStorage;
						const _errCode = `${errorcode.toString(16)}`;

						/*
						storage['msgId'] = intl.formatMessage({
							id             : 'room.sameTerminal',
							defaultMessage : 'The terminal with the same name enters the conference'
						}, {
							_errCode
						});
						*/
						storage['msgId'] = 'room.sameTerminal';

						break;
					}

					case 'callWaiting':
					{
						const { errorcode } = notification.data;

						const storage = window.sessionStorage;
						const _errCode = `${errorcode.toString(16)}`;

						/*
						storage['msgId'] = intl.formatMessage({
							id             : 'room.callWaiting',
							defaultMessage : 'The host starts call waiting and fails to join the meeting'
						}, {
							_errCode
						});
						*/
						storage['msgId'] = 'room.callWaiting';

						break;
					}

					case 'changeDisplayName':
					{
						// 入会时可能收到改名通知
						const { peerId, displayName } = notification.data;

						store.dispatch(peerActions.setPeerDisplayName(displayName, peerId));

						if (peerId === this.peerId) {

							store.dispatch(settingsActions.setDisplayName(displayName));
						}

						//  同时需要修改 room.layoutConfig 中的数据
						/*
						const _videoView = this._spotlights.layoutConfig && this._spotlights.layoutConfig.data.find((v) => v.peerId === peerId);
						if (_videoView) {

							_videoView.displayName = displayName;
							store.dispatch(roomActions.setLayoutConfig(this._spotlights.layoutConfig));
						}
						*/
						this._spotlights.layoutConfig && this._spotlights.layoutConfig.data.map((v) => {

							if (v.peerId === peerId) {

								v.displayName = displayName
							}
							return v;
						});
						store.dispatch(roomActions.setLayoutConfig(this._spotlights.layoutConfig));

						break;
					}

					case 'getMyDisplayName':
					{
						const { displayName } = notification.data;

						store.dispatch(settingsActions.setDisplayName(displayName));

						break;
					}

					case 'changePicture':
					{
						const { peerId, picture } = notification.data;

						store.dispatch(peerActions.setPeerPicture(peerId, picture));

						break;
					}

					case 'chatfMessage':
					{
						const { peerId, chatMessage } = notification.data;

						store.dispatch(chatActions.addResponseMessage({ ...chatMessage, peerId }));

						if (
							!store.getState().toolarea.toolAreaOpen ||
							(store.getState().toolarea.toolAreaOpen &&
							store.getState().toolarea.currentToolTab !== 'chat')
						) // Make sound
						{
							store.dispatch(roomActions.setToolbarsVisible(true));
							this._soundNotification();
						}

						break;
					}

					case 'moderator:clearChat':
					{
						store.dispatch(chatActions.clearChat());

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'moderator.clearChat',
									defaultMessage : 'Host cleared the chat'
								})
							}));

						break;
					}

					case 'sendFile':
					{
						const { peerId, magnetUri } = notification.data;

						store.dispatch(fileActions.addFile(peerId, magnetUri));

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'room.newFile',
									defaultMessage : 'New file available'
								})
							}));

						if (
							!store.getState().toolarea.toolAreaOpen ||
							(store.getState().toolarea.toolAreaOpen &&
							store.getState().toolarea.currentToolTab !== 'files')
						) // Make sound
						{
							store.dispatch(roomActions.setToolbarsVisible(true));
							this._soundNotification();
						}

						break;
					}

					case 'moderator:clearFileSharing':
					{
						store.dispatch(fileActions.clearFiles());

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'moderator.clearFiles',
									defaultMessage : 'Host  cleared the files'
								})
							}));

						break;
					}

					case 'producerScore':
					{
						const { producerId, score } = notification.data;

						store.dispatch(producerActions.setProducerScore(producerId, score));

						break;
					}

					case 'newPeer':
					{
						const {
							id,
							suid,
							displayName,
							picture,
							roles,
							videoMuted,
							audioMuted,
							audioAvailable,
							videoAvailable,
							joinMode,
							sfuTermType,
							batch
						} = notification.data;

						store.dispatch(peerActions.addPeer({
							id,
							suid,
							displayName,
							picture,
							roles,
							videoMuted,
							audioMuted,
							audioAvailable,
							videoAvailable,
							joinMode,
							sfuTermType,
							consumers: [],
							updateAt : Date.now()
						}));

						store.dispatch(peerActions.setPeerIsCameraOn(id, !videoMuted));

						const _suid = parseInt(suid);

						const { cohostSuids } = store.getState().room;
						if (cohostSuids.includes(_suid)) {

							store.dispatch(peerActions.setPeerCohost(id, true));
						}

						this._spotlights.newPeer(id, videoMuted, displayName, joinMode);
						// 批量入会不用通知
						if (!batch) {

							this._soundNotification();
							store.dispatch(requestActions.notify(
								{
									text : intl.formatMessage({
										id             : 'room.newPeer',
										defaultMessage : '{displayName} joined the room'
									}, {
										displayName
									})
								}));
						}

						break;
					}

					case 'peerClosed':
					{
						const { peerId } = notification.data;

						// 新增逻辑
						for (const _consumer of this._consumers.values())
						{
							if (peerId === _consumer.appData.peerId)
							{

								const consumerId = _consumer.id;

								_consumer.close();

								if (_consumer.hark != null)
									_consumer.hark.stop();

								this._consumers.delete(consumerId);

								store.dispatch(consumerActions.removeConsumer(consumerId, peerId));
							}
						}

						this._spotlights.closePeer(peerId);
						store.dispatch(peerActions.removePeer(peerId));

						this._spotlightPeerIds = this._spotlightPeerIds.filter((v) => v !== peerId);

						const { 
							windowPeerId, 
							activeSpeakerId 
						}  = store.getState().room;

						if (peerId === windowPeerId) {

							store.dispatch(roomActions.setWindowPeerId(null));
							store.dispatch(roomActions.setWindowVideoSource(''));
						}

						if (peerId === activeSpeakerId) {

							store.dispatch(roomActions.setRoomActiveSpeakerId(null));
						}

						break;
					}

					case 'consumerClosed':
					{
						const { 
							consumerId, 
							source 
						} = notification.data;
						const consumer = this._consumers.get(consumerId);

						if (consumer) {

							logger.debug('consumerClosed [consumerId:%s, source:%s] 关闭', consumerId, source);

							consumer.close();
							if (consumer.hark != null) {

								consumer.hark.off('volume_change');
								consumer.hark.stop();
								consumer.hark = null;
							}
							this._consumers.delete(consumerId);

							const { peerId } = consumer.appData;

							store.dispatch(consumerActions.removeConsumer(consumerId, peerId));

							if (consumer.kind === 'video') {

								const { 
									windowConsumerId, 
									fullScreenConsumerId 
								}  = store.getState().room;

								if (windowConsumerId && windowConsumerId === consumerId) {

									store.dispatch(roomActions.setWindowConsumerId(null));
								}

								if (fullScreenConsumerId && fullScreenConsumerId === consumerId) {

									store.dispatch(roomActions.closeConsumerFullscreen());
									this._fullScreenConsumerId = null;
								}

								if (consumer.appData.source === 'screen') {  // 停止接收共享

									// 标记会议室没有了共享视频
									store.dispatch(roomActions.setScreenSharing(false));
									await this._spotlights.handleShare(null);

									//停止共享后  如果有新窗口 且新窗口内是共享内容 则关闭新窗口
									const { 
										windowPeerId, 
										windowVideoSource 
									} = store.getState().room;

									if (windowPeerId && windowVideoSource === 'screen') {

										store.dispatch(roomActions.closeConsumerFullscreen());
										store.dispatch(roomActions.setWindowPeerId(null));
									}
								}
							}
						}

						break;
					}

					case 'consumerPaused':
					{
						const { consumerId } = notification.data;
						const consumer = this._consumers.get(consumerId);

						if (!consumer) {
							break;
						}
						store.dispatch(consumerActions.setConsumerPaused(consumerId, 'remote'));

						break;
					}

					case 'consumerResumed':
					{
						const { consumerId } = notification.data;
						const consumer = this._consumers.get(consumerId);

						if (!consumer) {
							break;
						}
						store.dispatch(consumerActions.setConsumerResumed(consumerId, 'remote'));

						break;
					}

					case 'consumerLayersChanged':
					{
						const { 
							consumerId, 
							spatialLayer, 
							temporalLayer 
						} = notification.data;
						const consumer = this._consumers.get(consumerId);

						if (!consumer) {
							break;
						}
                        // bingofree 修改
						if ([0, 1, 2].includes(spatialLayer) && [0, 1, 2].includes(temporalLayer)) {

							store.dispatch(consumerActions.setConsumerCurrentLayers(consumerId, spatialLayer, temporalLayer));
						}
							const _peerId = consumer.appData.peerId;
							if (_peerId) {

								await this._spotlights.updatePeerVideoMute(_peerId, false, 'consumerLayersChanged');
						}

						break;
					}

					case 'consumerScore':
					{
						const { 
							consumerId, 
							score 
						} = notification.data;

						store.dispatch(consumerActions.setConsumerScore(consumerId, score))

						break;
					}

					case 'peerMuteAll':   // 全部静音处理流程
					{
						const { 
							type, 
							muted 
						} = notification.data;
						
						if (type === 0) { // 音频

							const forceMuted = store.getState().me.forceMuted;
							if (forceMuted) {

								store.dispatch(meActions.setAllowUnMuted(!muted));
							}

							const _meIsModerator = meIsModeratorSelector(store.getState());
							const _meIsCohost =  meIsCohostSelector(store.getState());

							if (!_meIsModerator && !_meIsCohost) {

								// logger.debug(`peerMuteAll -> _meIsModerator:${_meIsModerator} _meIsCohost:${_meIsCohost}`);
								if (muted) {

									if (this._micProducer && !this._micProducer.paused) {

										this.muteMic();
									}
									store.dispatch(requestActions.notify({
										text: intl.formatMessage({
											id: 'message.micMuted',
											defaultMessage: 'Host  turns off your microphone'
										})
									}));
								} else {

									this.unmuteMic();
									store.dispatch(requestActions.notify({
										text: intl.formatMessage({
											id: 'message.micUnmuted',
											defaultMessage: 'Host turns on your microphone'
										})
									}));
								}
							}

							const { chairPeerId } = store.getState().room;

							// 同时将会中其它所有终端状态改为静音状态
							const _peerList = Object.values(store.getState().peers);
							_peerList.filter((peer) => [undefined, false].includes(peer.audioMuted) && peer.id !== chairPeerId && !peer.isCohost).map(
								peer => {
									store.dispatch(
										peerActions.setPeerAudioMute(
											peer.id,
											true
										)
									)
								}
							);

						} else if (type === 1) { // 视频

							if (muted) {

								await this.disableWebcam('peerMuteAll');
								store.dispatch(requestActions.notify({
									text: intl.formatMessage({
										id: 'message.webcamMuted',
										defaultMessage: 'The host turns off your camera'
									})
								}));

							} else {

								await this.updateWebcam({ start: true });
								store.dispatch(requestActions.notify({
									text: intl.formatMessage({
										id: 'message.webcamUnmuted',
										defaultMessage: 'The host turns on your camera'
									})
								}));
							}
						}

						break;
					}

					case 'peerMute':
					{

						this._queue.push(async () => {						

							const { type, muted } = notification.data;
							if (type === 0) { // 音频
	
								const forceMuted = store.getState().me.forceMuted;
								if (forceMuted) {
	
									store.dispatch(meActions.setAllowUnMuted(!muted));
								}
	
								if (muted) {
	
									if (this._micProducer && !this._micProducer.paused) {
	
										await this.muteMic();
									}
									store.dispatch(requestActions.notify({
										text: intl.formatMessage({
											id: 'message.micMuted',
											defaultMessage: 'Moderator turns off your microphone'
										})
									}));
								} else {
	
									await this.unmuteMic();
	
									store.dispatch(requestActions.notify({
										text: intl.formatMessage({
											id: 'message.micUnmuted',
											defaultMessage: 'Host turns on your microphone'
										})
									}));
	
								}
							} else if (type === 1) { // 视频
	
								if (muted) {
	
									await this.disableWebcam('peerMute');
	
									store.dispatch(requestActions.notify({
										text: intl.formatMessage({
											id: 'message.webcamMuted',
											defaultMessage: 'The host turns off your camera'
										})
									}))
	
								} else {
	
									await this.updateWebcam({ start: true });
	
									store.dispatch(requestActions.notify({
										text: intl.formatMessage({
											id: 'message.webcamUnmuted',
											defaultMessage: 'The host turns on your camera'
										})
									}));
								}
							}
	
						}).catch((err) => {
							logger.error('开启/关闭本地视频流(peerMute)失败 ["%o"]', err);
						});

						break;
					}

					case 'forceMute':  // 强制静音/解除静音
					{
						const { 
							muted: forceMuted 
						} = notification.data;

						const {
							allowUnMuted,
							forceMuted : _prevForceMuted
						} = store.getState().me;

						const {
							isForceMuteAll
						} = store.getState().room;

						const _meIsModerator = meIsModeratorSelector(store.getState());
						const _meIsCohost = meIsCohostSelector(store.getState());

						if (forceMuted) {

							// 如果是主持人,则不关闭麦克风,并设置状态
							logger.debug(`forceMute -> peerId:${this.peerId} prevForceMuted:${_prevForceMuted}`);

							if (_meIsModerator || _meIsCohost) {

								if (!isForceMuteAll) {

									store.dispatch(roomActions.setIsForceMuteAll(true)); // 主持人操作状态
								}
								
								if (_meIsModerator && _prevForceMuted) {

									store.dispatch(meActions.setForceMuted(false)); // 自己是否被强制静音了
								} else if (_meIsCohost && !_prevForceMuted) {

									store.dispatch(meActions.setForceMuted(true)); // 联席主持人
								}
							} else {

								if (isForceMuteAll) {

									store.dispatch(roomActions.setIsForceMuteAll(false));
								}

								if (!_prevForceMuted) {

									store.dispatch(meActions.setForceMuted(true));
								}

								if (!allowUnMuted) {  // 不被允许发言
									// 静音
									if (this._micProducer && !this._micProducer.paused) {

										await this.muteMic();
									}
									store.dispatch(requestActions.notify({
										text: intl.formatMessage({
											id: 'device.forceMuteAudio',
											defaultMessage: 'You have been forced to silence by the moderator'
										})
									}));
								}
							}
						} else {

							const {
								applyUnMuted,
								allowUnMuted
							} = store.getState().me;

							// 如果取消了强制静音,则将 applyUnMuted allowUnMuted 置为初始状态
							if (applyUnMuted) {

								store.dispatch(meActions.setApplyUnMuted(false));
							}

							if (allowUnMuted) {
							
								store.dispatch(meActions.setAllowUnMuted(false));
							}
							
							// 如果为主席
							if (_meIsModerator || _meIsCohost) {

								const {
									auditRaiseHandOpen,
									isForceMuteAll
								} = store.getState().room;

								if (isForceMuteAll) {
									
									store.dispatch(roomActions.setIsForceMuteAll(false));
								}

								if (auditRaiseHandOpen) {

									store.dispatch(roomActions.setAuditRaiseHandOpen(false));
								}
							}

							if (_prevForceMuted) {

								store.dispatch(meActions.setForceMuted(false));
							}

							//取消强制静音，重置会中所有终端的举手状态

							const _peerList = store.getState().peers ? Object.values(store.getState().peers) : [];
							_peerList.filter((peer) => peer.raisedHand).map(
								peer => {
									store.dispatch(
										peerActions.setPeerRaisedHand(
											peer.id,
											false,
											Date.now()  // 排序字段 raisedHandTimestamp
										)
									)
								}
							)

							store.dispatch(requestActions.notify({
								text: intl.formatMessage({
									id: 'device.freeMuteAudio',
									defaultMessage: 'The host has silenced you'
								})
							}))
						}

						break;
					}

					case 'peerAudioAllow':  // 举手申请返回结果
					{
						const { ispermission } = notification.data;
						logger.debug('收到申请回复 peerAudioAllow [ispermission: "%s"]', ispermission);

						// 修改申请状态及当前音频状态
						store.dispatch(meActions.setAllowUnMuted(ispermission));

						if (ispermission) {
							// 申请通过
							this.unmuteMic();

							store.dispatch(requestActions.notify(
								{
									text: intl.formatMessage({
										id: 'device.peerAudioAllow',
										defaultMessage: 'You have been forced to silence by the host'
									})
								}));
						} else {
							// 申请被拒
							store.dispatch(requestActions.notify(
								{
									text: intl.formatMessage({
										id: 'device.peerAudioRefused',
										defaultMessage: 'The host has silenced you'
									})
								}
							))
						}
						store.dispatch(meActions.setApplyUnMuted(false));
						// 取消申请(调用 webappRaiseHand 通知会中其它终端,申请发言流程结束)
						await this.requestRaiseHand(false, 1);
						break;
					}
                    // 过期事件
					case 'raisedHand':
					{
						const {
							peerId,
							raisedHand,
							raisedHandTimestamp
						} = notification.data;

						store.dispatch(
							peerActions.setPeerRaisedHand(
								peerId,
								raisedHand,
								raisedHandTimestamp
							)
						);

						const { displayName } = store.getState().peers[peerId];

						let text;

						if (raisedHand)
						{
							text = intl.formatMessage({
								id             : 'room.raisedHand',
								defaultMessage : '{displayName} request to restore audio'
							}, {
								displayName
							});
						}
						else
						{
							text = intl.formatMessage({
								id             : 'room.loweredHand',
								defaultMessage : '{displayName} abandon audio request'
							}, {
								displayName
							});
						}

						if (displayName)
						{
							store.dispatch(requestActions.notify(
								{
									text
								}));
						}

						this._soundNotification();

						break;
					}

					case 'peerRaisehand':  // 会议室中举手终端
					{
						const {
							handuppeerId: peerId,
							ishandup: raisedHand,
							auto
						} = notification.data;

						logger.debug('通知内容 peerRaisehand [peerId: "%s",raisedHand: "%s"]', peerId, raisedHand);

						// 判断当前终端是否为主持人
						const { chairPeerId, auditRaiseHandOpen }  = store.getState().room;
						if (this.peerId === chairPeerId) {

							if (!auditRaiseHandOpen && raisedHand) {

								// 将当前举手的终端 peerId 记录下来(在对话框中需要将其清除掉)
								store.dispatch(roomActions.setRaiseHandPeerId(peerId));
								store.dispatch(roomActions.setAuditRaiseHandOpen(true));

							} else if (auditRaiseHandOpen && !raisedHand) {

								// 确认对话框打开举手终端放弃举手
								store.dispatch(roomActions.setAuditRaiseHandOpen(false));
							}
						}

						store.dispatch(
							peerActions.setPeerRaisedHand(
								peerId,
								raisedHand,
								Date.now()  // 排序字段 raisedHandTimestamp
							)
						);

						const _raisedHandPeer = store.getState().peers[peerId];

						if (_raisedHandPeer) {

							const { displayName } = _raisedHandPeer;
							let text;

							if (raisedHand)
							{
								text = intl.formatMessage({
									id             : 'room.raisedHand',
									defaultMessage : '{displayName} raised their hand'
								}, {
									displayName
								});
							}
							else
							{
								text = intl.formatMessage({
									id             : 'room.loweredHand',
									defaultMessage : '{displayName} cancels raising his hand'
								}, {
									displayName
								});
							}

							// auto : 0:主动放弃;1:被动放弃 只有主动放弃才会有提示信息
							if (displayName && auto === 0)
							{
								store.dispatch(requestActions.notify({
									text
								}));
							}

							this._soundNotification();
						}

						break;
					}

					case 'rollcall':
					{
						const { isRollCall } = notification.data;

						logger.debug('点名通知 rollcall [isRollCall: "%s"]', isRollCall);

						store.dispatch(roomActions.setRollcall(isRollCall));

						let rollcallText = isRollCall ?
							intl.formatMessage({
								id: 'rollcall.starting',
								defaultMessage: 'Roll call has begun. Please get ready'
							})
							:
							intl.formatMessage({
								id: 'rollcall.end',
								defaultMessage: 'Roll call is over'
							});

						store.dispatch(requestActions.notify({
							text: rollcallText
						}));

						let _rollcall = store.getState().room.rollcall;
						let _myScreenSharing = store.getState().room.myScreenSharing;

						// 会控开启点名并且会中有自己在共享视频时结束共享
						if (_myScreenSharing && _rollcall) {

							this.disableScreenSharing();
						}

						break;
					}

					case 'recordVideo':
					{
							const { 
								isRecord, 
								errorCode, 
								failReason 
							} = notification.data;

							logger.debug('录制通知 recordVideo [isRecord: "%s", errorCode: "%s", failReason: "%s"]', isRecord, errorCode, failReason);

							const _errCode = parseInt(errorCode);

							store.dispatch(roomActions.setRecordErrCode(_errCode));

							const _meIsModerator = meIsModeratorSelector(store.getState());
							const _meIsCohost =  meIsCohostSelector(store.getState());	

							if (_meIsModerator || _meIsCohost) {
								// 当前主持人终端正在/停止(录制)
								store.dispatch(roomActions.setMeIsRecording(isRecord));
							} else {

								const { meIsRecording } = store.getState().room;
								if (meIsRecording) {

									store.dispatch(roomActions.setMeIsRecording(false));
								}

								store.dispatch(roomActions.setHasRecordingPrompt(isRecord));
							}

							if (_errCode > 0) {

								// 主持人开启/停止录制的时候返回错误处理
								if (_meIsModerator || _meIsCohost) {
									// 错误提示
									const _lang = store.getState().intl.lang;
									const _errMsg = getErrMesage(_errCode, _lang, false);
									// 显示错误提示
									store.dispatch(requestActions.notify({
										text: _errMsg
									}));
								}
							} else {

								// 当前会议室是否有录制
								store.dispatch(roomActions.setHasRecording(isRecord));
								
								if (_meIsModerator || _meIsCohost) {

									// 如果为结束录制,则需要提示
									if (!isRecord) {

										const recordText = intl.formatMessage({
											id: 'label.stopRecordNotify',
											defaultMessage: 'Recording is over, please go to the background to view the conference video'
										})

										store.dispatch(requestActions.notify({
											text: recordText
										}));
									}
								}
								else {

									const recordText = isRecord ?
										intl.formatMessage({
											id: 'record.starting',
											defaultMessage: 'Record has begun. Please get ready'
										})
										:
										intl.formatMessage({
											id: 'record.end',
											defaultMessage: 'Record is over'
										});

									store.dispatch(requestActions.notify({
										text: recordText
									}));
								}
							}

						break;
					}

					case 'voteUpdated':
					{
						// 'startVote', 'endVote', 'deleteVote', 'back'

						const { 
							type, 
							userId, 
							surveyId 
						} = notification.data;

						// 消息提示框内容索引
						store.dispatch(roomActions.setSnackBarTipType(type));
						// 开启消息提示框
						store.dispatch(roomActions.setSnackBarOpen(true));

						store.dispatch(roomActions.setVoteUpdated({ 
							type, 
							userId, 
							surveyId 
						}));

						break;
					}

					case 'moderator:mute':
					{
						if (this._micProducer && !this._micProducer.paused)
						{
							await this.muteMic();

							store.dispatch(requestActions.notify(
								{
									text : intl.formatMessage({
										id             : 'moderator.muteAudio',
										defaultMessage : 'Host muted your audio'
									})
								}));
						}

						break;
					}

					case 'moderator:stopVideo':
					{
						await this.disableWebcam('moderator:stopVideo');

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'moderator.muteVideo',
									defaultMessage : 'Host stopped your video'
								})
							}));

						break;
					}

					case 'moderator:stopScreenSharing':
					{
						await this.disableScreenSharing();

						store.dispatch(requestActions.notify(
							{
								text : intl.formatMessage({
									id             : 'moderator.stopScreenSharing',
									defaultMessage : 'Host stopped your screen sharing'
								})
							}));

						break;
					}

					case 'moderator:kick':
					{
						// Need some feedback
						this.close();

						break;
					}

					case 'moderator:lowerHand':
					{
						this.setRaisedHand(false);

						break;
					}

					case 'gotRole':
					{
						const { 
							peerId, 
							role 
						} = notification.data;

						if (peerId === this._peerId)
						{
							store.dispatch(meActions.addRole(role));

							store.dispatch(requestActions.notify(
								{
									text : intl.formatMessage({
										id             : 'roles.gotRole',
										defaultMessage : 'You got the role: {role}'
									}, {
										role
									})
								}));
						}
						else
							store.dispatch(peerActions.addPeerRole(peerId, role));

						break;
					}

					case 'lostRole':
					{
						const { 
							peerId, 
							role 
						} = notification.data;

						if (peerId === this._peerId)
						{
							store.dispatch(meActions.removeRole(role));

							store.dispatch(requestActions.notify(
								{
									text : intl.formatMessage({
										id             : 'roles.lostRole',
										defaultMessage : 'You lost the role: {role}'
									}, {
										role
									})
								}));
						}
						else
							store.dispatch(peerActions.removePeerRole(peerId, role));

						break;
					}

					// 接收字幕的内容
					case 'subtitleUpdated':
					{
						// 'NoticeConvertWrite'
						const {
							displayName,
							speech,
							errorCode,
							isFinal
						} = notification.data;

						const _errorCode = parseInt(errorCode);

						if ([458752, 458753, 458754, 458755, 458756, 0x04008F, 0x040090].includes(_errorCode)) {

							// 通知 mc 禁用实时字幕
							await this.webappTranslationControl(2);

							const { 
								subtitleErrCode, 
								subtitleOpen 
							}  = store.getState().room;

							if (subtitleErrCode !== _errorCode) {

								store.dispatch(roomActions.setSubtitleErrCode(_errorCode));

								if (subtitleOpen) {
									// 关闭字幕
									store.dispatch(roomActions.setSubtitleOpen(false));

									const _lang = store.getState().intl.lang;
									const _errMsg = getErrMesage(_errorCode, _lang, false);
									// 显示错误提示
									store.dispatch(requestActions.notify({
										text: _errMsg
									}));
								}
							}

						} else {

							store.dispatch(roomActions.setSubtitleErrCode(0));

							if (this._clearSubtitleTimer) clearTimeout(this._clearSubtitleTimer);

							store.dispatch(subtitleActions.addSubtitle({
								displayName,
								text : speech,
								isFinal
							}));

							// 5 秒之后清空内容
							this._clearSubtitleTimer = setTimeout(() => {

								store.dispatch(subtitleActions.removeAllSubtitles());

							}, 1000 * 5);
						}

						break;
					}
					// 接收开启字幕的实时通知 (允许/禁止)终端显示字幕
					case 'changePermissionTranslation':
					{
						// 开启/关闭实时字幕(1:隐藏; 2:显示)
						const { opType } = notification.data;

						if (opType === 1) {

							const { 
								subtitleOpen, 
								chairPeerId, 
								subtitleEnable 
							} = store.getState().room;

							const _meIsModerator = meIsModeratorSelector(store.getState());
							const _meIsCohost = meIsCohostSelector(store.getState());	

							if (!_meIsModerator && !_meIsCohost) {

								if (subtitleOpen) {

									store.dispatch(roomActions.setSubtitleOpen(false));
								}

								if (subtitleEnable === 2) {

									// 如果禁用实时字幕,则提示终端
									if (this._subtitleTipCount > 0) {
	
										store.dispatch(requestActions.notify({
											text: intl.formatMessage({
												id             : 'message.disableSubtitle',
												defaultMessage : 'The host has forbidden to open real-time subtitles'
											})
										}));
									}
	
									++this._subtitleTipCount;
	
								}
							}
						}

						store.dispatch(roomActions.setSubtitleState(opType));

						break;
					}

					// 接收字幕的状态通知(主要处理出错)
					case 'subtitleTranslationState':
					{
						// 开启/关闭实时字幕
						const { 
							state, 
							errorCode, 
							failReason 
						} = notification.data;

						const { 
							subtitleErrCode, 
							subtitleOpen 
						}  = store.getState().room;

						if (subtitleErrCode !== errorCode) {

							store.dispatch(roomActions.setSubtitleErrCode(errorCode));
						}

						if ([458752, 458753, 458754, 458755, 458756, 0x04008F, 0x040090].includes(errorCode)) {

							// 通知 mc 禁用实时字幕
							// await this.webappTranslationControl(2);

							// logger.debug(`subtitleTranslationState  : subtitleErrCode:${subtitleErrCode} errorCode: ${errorCode}`);

							// if (subtitleErrCode !== errorCode) {

								if (subtitleOpen) {
									// 关闭字幕
									store.dispatch(roomActions.setSubtitleOpen(false));

									const _lang = store.getState().intl.lang;
									const _errMsg = getErrMesage(errorCode, _lang, false);
									// 显示错误提示
									store.dispatch(requestActions.notify({
										text: _errMsg
									}));
								}
							// }
						}	

						break;
					}

					case 'termStatus':
					{
						// status为3时 -> 0-不存在麦克风；1-麦克风静音；2-麦克风非静音
						// status为4时 -> 0-不存在视频设备；1-存在视频设备

						const { statusPeerId, status, subStatus } = notification.data;

						if ([3, 4].includes(status) && [0, 1, 2].includes(subStatus)) {

							if (status === 3) {

								if (subStatus === 1) {

									store.dispatch(peerActions.setPeerAudioAvailable(statusPeerId, false));
								} else if (subStatus === 2) {

									store.dispatch(peerActions.setPeerAudioAvailable(statusPeerId, true));
								}
							} else if (status === 4) {

								if (subStatus === 0) {

									store.dispatch(peerActions.setPeerVideoAvailable(statusPeerId, false));
								} else if (subStatus === 1) {

									store.dispatch(peerActions.setPeerVideoAvailable(statusPeerId, true));
								}
							}
						}

						break;
					}

					default:
					{
						logger.error(
							'unknown notification.method "%s"', notification.method);
					}
				}
			}
			catch (error)
			{
				logger.error('error on socket "notification" event [method:%s data:%s  error:%o]', notification.method, JSON.stringify(notification.data), error.stack);

				store.dispatch(requestActions.notify(
					{
						type : 'error',
						text : intl.formatMessage({
							id             : 'socket.requestError',
							defaultMessage : 'Error on server request'
						})
					}));
			}

		});
	}

	async _joinRoom({ joinVideo, joinAudio })
	{
		logger.debug('start _joinRoom()');

		const { displayName } = store.getState().settings;
		const { picture, userInfo } = store.getState().me;

		try
		{
			const _suid = userInfo && userInfo.suid;
			// 入会之后将自己加入
			store.dispatch(peerActions.addPeer({
				id         : this._peerId,
				suid       : _suid,
				displayName,
				videoMuted : true,
				picture,
				roles      : ['normal'],
				consumers  : [],
				updateAt : Date.now()
			}));

			const { cohostSuids } = store.getState().room;
			if (cohostSuids.includes(_suid)) {

				store.dispatch(peerActions.setPeerCohost(this._peerId, true));
			}

			// 将本地 peer 加入 spotlights
			await this._spotlights.handleLocal(this._peerId, true);

			this._torrentSupport = WebTorrent.WEBRTC_SUPPORT;

			this._webTorrent = this._torrentSupport && new WebTorrent({
				tracker : {
					rtcConfig : {
						iceServers : this._turnServers
					}
				}
			});

			this._webTorrent.on('error', (error) =>
			{
				logger.error('Filesharing [error:"%o"]', error);

				store.dispatch(requestActions.notify(
					{
						type : 'error',
						text : intl.formatMessage({
							id             : 'filesharing.error',
							defaultMessage : 'There was a filesharing error'
						})
					}));
			});

			this._mediasoupDevice = new mediasoupClient.Device();
			const routerRtpCapabilities = await this.sendRequest('getRouterRtpCapabilities');

			routerRtpCapabilities.headerExtensions = routerRtpCapabilities.headerExtensions
				.filter((ext) => ext.uri !== 'urn:3gpp:video-orientation');

			await this._mediasoupDevice.load({ routerRtpCapabilities });

			if (this._produce) {

				const transportInfo = await this.sendRequest(
					'createWebRtcTransport',
					{
						direction : 'send',
						forceTcp  : this._forceTcp,
						producing : true,
						consuming : false
					});

				const {
					id,
					iceParameters,
					iceCandidates,
					dtlsParameters
				} = transportInfo;
				this._iceParameters = iceParameters;

				this._sendTransport = this._mediasoupDevice.createSendTransport({
					id,
					iceParameters,
					iceCandidates,
					dtlsParameters,
					iceServers             : this._turnServers,
					// TODO: Fix for issue #72
					//iceTransportPolicy     : this._device.flag === 'firefox' && this._turnServers ? 'relay' : undefined,
					iceTransportPolicy     : this._turnServers ? 'relay' : undefined,
					proprietaryConstraints : PC_PROPRIETARY_CONSTRAINTS
				});

				this._sendTransport.on(
					'connect', ({ dtlsParameters }, callback, errback) => // eslint-disable-line no-shadow
					{
						this.sendRequest(
							'connectWebRtcTransport',
							{
								transportId : this._sendTransport.id,
								dtlsParameters
							})
							.then(callback)
							.catch(errback);
					});

				this._sendTransport.on(
					'connectionstatechange', (connectState) =>
					{
						if(this._sendTransport.closed) {
							return;
						}

						switch (connectState)
						{
							case 'disconnected':  // 网络断开时退出
							if (window.config.returnLogin) {

								this.close();
							} else {

								this.restartIce(this._sendTransport, this._sendRestartIce, 2000);
							}
							break;
						case 'failed':
							this.restartIce(this._sendTransport, this._sendRestartIce, 2000);
							break;

							default:
								clearTimeout(this._sendRestartIce.timer);
								break;
						}
					});

				this._sendTransport.on(
					'produce', async ({ kind, rtpParameters, appData }, callback, errback) =>
					{
						try
						{
							// eslint-disable-next-line no-shadow
							// id 由服务端生成的 produceId
							const { id } = await this.sendRequest(
								'produce',
								{
									transportId : this._sendTransport.id,
									kind,
									rtpParameters,
									appData
								});

							callback({ id });
						}
						catch (error)
						{
							errback(error);
						}
					});
			}

			const transportInfo = await this.sendRequest(
				'createWebRtcTransport',
				{
					direction : 'recv',
					forceTcp  : this._forceTcp,
					producing : false,
					consuming : true
				});

			const {
				id,
				iceParameters,
				iceCandidates,
				dtlsParameters
			} = transportInfo;
			this._iceParameters = iceParameters;

			this._recvTransport = this._mediasoupDevice.createRecvTransport({
				id,
				iceParameters,
				iceCandidates,
				dtlsParameters,
				iceServers         : this._turnServers,
				// TODO: Fix for issue #72
				//iceTransportPolicy : this._device.flag === 'firefox' && this._turnServers ? 'relay' : undefined
				iceTransportPolicy : this._turnServers ? 'relay' : undefined
			});

			this._recvTransport.on(
				'connect', ({ dtlsParameters }, callback, errback) => // eslint-disable-line no-shadow
			{
				this.sendRequest(
					'connectWebRtcTransport',
					{
						transportId: this._recvTransport.id,
						dtlsParameters
					})
					.then(callback)
					.catch(errback);
			});

			this._recvTransport.on(
				'connectionstatechange', (connectState) => {
				if (this._recvTransport.closed) {
					return;
				}

				switch (connectState) {
					case 'disconnected':
					case 'failed':
						this.restartIce(this._recvTransport, this._recvRestartIce, 2000);
						break;

					default:
						clearTimeout(this._recvRestartIce.timer);
						break;
				}
			});

            // 初始化设备列表
			if (JSON.stringify(this._audioDevices) === '{}') {

				await this._updateAudioDevices();
				const { selectedAudioDevice } = store.getState().settings;
				if (!selectedAudioDevice && Object.keys(this._audioDevices).length >  0) {

					store.dispatch(
						settingsActions.setSelectedAudioDevice(
							Object.keys(this._audioDevices)[0]
						)
					);
				}
			}

			if (JSON.stringify(this._webcams) === '{}') {

				await this._updateWebcams();
				const { selectedWebcam } = store.getState().settings;
				if (!selectedWebcam && Object.keys(this._webcams).length >  0) {

					store.dispatch(
						settingsActions.setSelectedWebcamDevice(
							Object.keys(this._webcams)[0]
						)
					);
				}
			}

			if (JSON.stringify(this._audioOutputDevices) === '{}') {

				await this._updateAudioOutputDevices();
				const { selectedAudioOutputDevice } = store.getState().settings;
				if (!selectedAudioOutputDevice && Object.keys(this._audioOutputDevices).length >  0) {

					store.dispatch(
						settingsActions.setSelectedAudioOutputDevice(
							Object.keys(this._audioOutputDevices)[0]
						)
					);
				}
			}

			const _canSendMic = Object.keys(this._audioDevices).length >  0 ? true : false;
			const _canSendWebcam = Object.keys(this._webcams).length > 0 ? true : false;

			logger.debug(`当前设备可用状态: _canSendMic:${_canSendMic} _canSendWebcam:${_canSendWebcam}`);

			// Set our media capabilities. joinRoom 之后，完成 canShareScreen 的赋值
			store.dispatch(meActions.setMediaCapabilities({
				canSendMic     : this._mediasoupDevice.canProduce('audio'),
				canSendWebcam  : this._mediasoupDevice.canProduce('video'),
				canShareScreen : this._mediasoupDevice.canProduce('video') &&  this._screenSharing.isScreenShareAvailable(),
				canShareFiles : this._torrentSupport
			}));

			const {
				authenticated,
				roles,
				peers,
				tracker,
				roomPermissions,
				allowWhenRoleMissing,
				chatHistory,
				fileHistory,
				locked,
				lobbyPeers,
				accessCode
			} = await this.sendRequest(
				'join',
				{
					displayName     : displayName,
					picture         : picture,
					audioAvailable  : _canSendMic,
					videoAvailable  : _canSendWebcam,

					rtpCapabilities : this._mediasoupDevice.rtpCapabilities
				});

			tracker && (this._tracker = tracker);

			//store.dispatch(meActions.setLoggedIn(authenticated)); bingofree 修改 用户登录成功，即认证通过

			store.dispatch(roomActions.setRoomPermissions(roomPermissions));

			if (allowWhenRoleMissing) {

				store.dispatch(roomActions.setAllowWhenRoleMissing(allowWhenRoleMissing));
			}
			const myRoles = store.getState().me.roles;

			for (const role of roles)
			{
				if (!myRoles.includes(role))
				{
					store.dispatch(meActions.addRole(role));

					store.dispatch(requestActions.notify(
						{
							text : intl.formatMessage({
								id             : 'roles.gotRole',
								defaultMessage : 'You got the role: {role}'
							}, {
								role
							})
						}));
				}
			}

			for (const peer of peers)
			{

				store.dispatch(peerActions.addPeer({
					...peer,
					consumers: [],
					updateAt : Date.now()
				}));
			}

			logger.debug(`join -> 参会人列表:${JSON.stringify(peers)}`);

			(chatHistory.length > 0) && store.dispatch(chatActions.addChatHistory(chatHistory));

			(fileHistory.length > 0) && store.dispatch(fileActions.addFileHistory(fileHistory));

			locked ?
				store.dispatch(roomActions.setRoomLocked()) :
				store.dispatch(roomActions.setRoomUnLocked());

			(lobbyPeers.length > 0) && lobbyPeers.forEach((peer) => {
				store.dispatch(
					lobbyPeerActions.addLobbyPeer(peer.id));
				store.dispatch(
					lobbyPeerActions.setLobbyPeerDisplayName(peer.displayName, peer.id));
				store.dispatch(
					lobbyPeerActions.setLobbyPeerPicture(peer.picture, peer.id));
			});

			(accessCode != null) && store.dispatch(roomActions.setAccessCode(accessCode));

			store.dispatch(roomActions.setRoomState('connected'));
			// Clean all the existing notifications.
			store.dispatch(notificationActions.removeAllNotifications());

			if (peers.length > 0) {

				await this._spotlights.addPeers(peers);
			}

			// Don't produce if explicitly requested to not to do it.
			if (this._produce && joinVideo) {

				await this.updateWebcam({ 
					init: true, 
					start: true 
				});
			}

			// 如果音频设备可用
			if (this._mediasoupDevice.canProduce('audio')) {

				await this.updateMic({ 
					start: true, 
					paused: (!joinAudio || this._muted) 
				});
				// 如果需要静音
				/*
				if (!joinAudio || this._muted) {

					await this.muteMic();
				}
				*/
			}

			this._webcamProducerCreated = true;

			if (this._webcamProducer && this._isFirstRunSetMaxQLevel && this._recvMaxQLevel > 0) {

				const _maxSpatialLayer = QLevelMapSpatialLayer.get(this._recvMaxQLevel);

				if ([0, 1, 2].includes(_maxSpatialLayer)) {

					await this._webcamProducer.setMaxSpatialLayer(_maxSpatialLayer);
				}

				this._isFirstRunSetMaxQLevel = false;
				this._recvMaxQLevel = -1;
			}

			logger.debug('end _joinRoom() joined');

			// 会中镜头不允许其他人控制
			await this.reportTermStatus(6, 3);

		} catch (error)  {

			logger.error('_joinRoom() [error:"%o"]', error);

			store.dispatch(requestActions.notify({
				type : 'error',
				text : intl.formatMessage({
					id             : 'room.cantJoin',
					defaultMessage : 'Unable to join the room'
				})
			}));

			this.close();
		}
	}

	async lockRoom()
	{
		logger.debug('lockRoom()');

		try
		{
			await this.sendRequest('lockRoom');

			store.dispatch(
				roomActions.setRoomLocked());

			store.dispatch(requestActions.notify(
				{
					text : intl.formatMessage({
						id             : 'room.youLocked',
						defaultMessage : 'You locked the room'
					})
				}));
		}
		catch (error)
		{
			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : intl.formatMessage({
						id             : 'room.cantLock',
						defaultMessage : 'Unable to lock the room'
					})
				}));

			logger.error('lockRoom() [error:"%o"]', error);
		}
	}

	async unlockRoom()
	{
		logger.debug('unlockRoom()');

		try
		{
			await this.sendRequest('unlockRoom');

			store.dispatch(
				roomActions.setRoomUnLocked());

			store.dispatch(requestActions.notify(
				{
					text : intl.formatMessage({
						id             : 'room.youUnLocked',
						defaultMessage : 'You unlocked the room'
					})
				}));
		}
		catch (error)
		{
			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : intl.formatMessage({
						id             : 'room.cantUnLock',
						defaultMessage : 'Unable to unlock the room'
					})
				}));

			logger.error('unlockRoom() [error:"%o"]', error);
		}
	}

	async setAccessCode(code)
	{
		logger.debug('setAccessCode()');

		try
		{
			await this.sendRequest('setAccessCode', { accessCode: code });

			store.dispatch(
				roomActions.setAccessCode(code));

			store.dispatch(requestActions.notify(
				{
					text : 'Access code saved.'
				}));
		}
		catch (error)
		{
			logger.error('setAccessCode() [error:"%o"]', error);
			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : 'Unable to set access code.'
				}));
		}
	}

	async setJoinByAccessCode(value)
	{
		logger.debug('setJoinByAccessCode()');

		try
		{
			await this.sendRequest('setJoinByAccessCode', { joinByAccessCode: value });

			store.dispatch(
				roomActions.setJoinByAccessCode(value));

			store.dispatch(requestActions.notify(
				{
					text : `You switched Join by access-code to ${value}`
				}));
		}
		catch (error)
		{
			logger.error('setAccessCode() [error:"%o"]', error);
			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : 'Unable to set join by access code.'
				}));
		}
	}

	async addExtraVideo(videoDeviceId)
	{
		logger.debug(
			'addExtraVideo() [videoDeviceId:"%s"]',
			videoDeviceId
		);

		store.dispatch(
			roomActions.setExtraVideoOpen(false));

		if (!this._mediasoupDevice.canProduce('video'))
		{
			logger.error('addExtraVideo() | cannot produce video');

			return;
		}

		let track;

		store.dispatch(
			meActions.setWebcamInProgress(true));

		try
		{
			const device = this._webcams[videoDeviceId];
			const resolution = store.getState().settings.resolution;

			if (!device)
				throw new Error('no webcam devices');

			const stream = await navigator.mediaDevices.getUserMedia(
				{
					video :
					{
						deviceId : { ideal: videoDeviceId },
						...VIDEO_CONSTRAINS[resolution]
					}
				});

			([ track ] = stream.getVideoTracks());

			let producer;

			if (this._useSimulcast)
			{
				// If VP9 is the only available video codec then use SVC.
				const firstVideoCodec = this._mediasoupDevice
					.rtpCapabilities
					.codecs
					.find((c) => c.kind === 'video');

				let encodings;

				if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9')
					encodings = VIDEO_KSVC_ENCODINGS;
				else if ('simulcastEncodings' in window.config)
					encodings = window.config.simulcastEncodings;
				else
					encodings = VIDEO_SIMULCAST_ENCODINGS;


				producer = await this._sendTransport.produce(
					{
						track,
						encodings,
						codecOptions :
						{
							videoGoogleStartBitrate : 1000
						},
						appData :
						{
							source : 'extravideo'
						}
					});
			}
			else
			{
				producer = await this._sendTransport.produce({
					track,
					appData :
					{
						source : 'extravideo'
					}
				});
			}

			this._extraVideoProducers.set(producer.id, producer);

			store.dispatch(producerActions.addProducer(
				{
					id            : producer.id,
					deviceLabel   : device.label,
					source        : 'extravideo',
					paused        : producer.paused,
					track         : producer.track,
					rtpParameters : producer.rtpParameters,
					codec         : producer.rtpParameters.codecs[0].mimeType.split('/')[1]
				}));

			// store.dispatch(settingsActions.setSelectedWebcamDevice(deviceId));

			await this._updateWebcams();

			producer.on('transportclose', () =>
			{
				this._extraVideoProducers.delete(producer.id);

				producer = null;
			});

			producer.on('trackended', () =>
			{
				/*
				store.dispatch(requestActions.notify({
					//type : 'error',
					text : intl.formatMessage({
						id             : 'devices.cameraDisconnected',
						defaultMessage : 'Camera disconnected'
					})
				}));
				*/

				this.disableExtraVideo(producer.id)
					.catch(() => {});
			});

			logger.debug('addExtraVideo() succeeded');
		}
		catch (error)
		{
			logger.error('addExtraVideo() [error:"%o"]', error);

			store.dispatch(requestActions.notify(
				{
					type : 'error',
					text : intl.formatMessage({
						id             : 'devices.cameraError',
						defaultMessage : 'An error occurred while accessing your camera'
					})
				}));

			if (track)
				track.stop();
		}

		store.dispatch(
			meActions.setWebcamInProgress(false));
	}

	async disableMic()
	{
		logger.debug('disableMic()');

		if (!this._micProducer) {
			return;
		}

		store.dispatch(meActions.setAudioInProgress(true));

		this._micProducer.close();

		store.dispatch(producerActions.removeProducer(this._micProducer.id));

		try
		{
			await this.sendRequest('closeProducer', { 
				producerId: this._micProducer.id 
			});
		}
		catch (error)
		{
			logger.error('disableMic() [error:"%o"]', error);
		}

		this._micProducer = null;

        // 设置状态
		store.dispatch(peerActions.setPeerAudioMute(this.peerId, true));
		store.dispatch(meActions.setAudioInProgress(false));
	}

	async updateScreenSharing({
		start = false,
		newResolution = null,
		newFrameRate = null
	} = {})
	{
		logger.debug('updateScreenSharing() [start:"%s"]', start);

		let track;
		try {

			const available = this._screenSharing.isScreenShareAvailable();

			if (!available) {
				throw new Error('screen sharing not available');
			}
			if (!this._mediasoupDevice.canProduce('video')) {
				throw new Error('cannot produce video');
			}
			if (newResolution) {
				store.dispatch(settingsActions.setScreenSharingResolution(newResolution));
			}
			if (newFrameRate) {
				store.dispatch(settingsActions.setScreenSharingFrameRate(newFrameRate));
			}

			store.dispatch(meActions.setScreenShareInProgress(true));

			const {
				screenSharingResolution,
				screenSharingFrameRate
			} = store.getState().settings;

			if (start) {

				// 弹出对话窗时,执行暂停在这一行，确定继续向下执行，取消则跳至 catch 代码段
				const stream = await this._screenSharing.start({
					...VIDEO_CONSTRAINS[screenSharingResolution],
					frameRate : screenSharingFrameRate
				});

				const _screenConsumer = screenConsumerSelector(store.getState());
				if (_screenConsumer && _screenConsumer.id) {

					logger.debug('房间里已经有共享视频了...', _screenConsumer);
					store.dispatch(meActions.setScreenShareInProgress(false));
					this._screenSharing.stop();
					// 共享视频被占用
					store.dispatch(requestActions.notify({
						type: 'error',
						text: intl.formatMessage({
							id: 'devices.screenSharingOccupied',
							defaultMessage: 'Screen sharing occupied'
						})
					}));

					return;
				}

				([ track ] = stream.getVideoTracks());

				if (this._useSharingSimulcast) {
					// If VP9 is the only available video codec then use SVC.
					const firstVideoCodec = this._mediasoupDevice
						.rtpCapabilities
						.codecs
						.find((c) => c.kind === 'video');

					let encodings;

					if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9') {
						encodings = VIDEO_SVC_ENCODINGS;
					}
					else if ('simulcastEncodings' in window.config) {

						encodings = window.config.simulcastEncodings.map((encoding) => ({ ...encoding, dtx: true }));
					}
					else {
						encodings = VIDEO_SIMULCAST_ENCODINGS.map((encoding) => ({ ...encoding, dtx: true }));
					}

					this._screenSharingProducer = await this._sendTransport.produce({
						track,
						encodings,
						codecOptions :
						{
							videoGoogleStartBitrate : 1000
						},
						appData :
						{
							source : 'screen'
						}
					});
				}
				else
				{
					this._screenSharingProducer = await this._sendTransport.produce({
						track,
						appData :
						{
							source : 'screen'
						}
					});
				}

				store.dispatch(producerActions.addProducer({
					id            : this._screenSharingProducer.id,
					deviceLabel   : 'screen',
					source        : 'screen',
					paused        : this._screenSharingProducer.paused,
					track         : this._screenSharingProducer.track,
					rtpParameters : this._screenSharingProducer.rtpParameters,
					codec         : this._screenSharingProducer.rtpParameters.codecs[0].mimeType.split('/')[1]
				}));

				this._screenSharingProducer.on('transportclose', () => {

					this._screenSharingProducer = null;
				});

				this._screenSharingProducer.on('trackended', () => {

					store.dispatch(requestActions.notify({
						//type : 'error',
						text : intl.formatMessage({
							id             : 'devices.screenSharingDisconnected',
							defaultMessage : 'Screen sharing disconnected'
						})
					}));

					this.disableScreenSharing();
				});
			}
			else if (this._screenSharingProducer) {

				({ track } = this._screenSharingProducer);

				await track.applyConstraints({
					...VIDEO_CONSTRAINS[screenSharingResolution],
					frameRate : screenSharingFrameRate
				});
			}

			store.dispatch(roomActions.setMyScreenSharing(true));
		}
		catch (error) {

			logger.error('updateScreenSharing() [error:"%o"]', error);
			/*
			共享后又了取消共享信息提示
			store.dispatch(requestActions.notify({
				type : 'error',
				text : intl.formatMessage({
					id             : 'devices.screenSharingError',
					defaultMessage : 'An error occurred while accessing your screen'
				})
			}));
			*/
			if (track) {
				track.stop();
			}
		}

		store.dispatch(meActions.setScreenShareInProgress(false));
	}

	async disableScreenSharing()
	{
		logger.debug('disableScreenSharing()');

		if (!this._screenSharingProducer) {
			return;
		}

		store.dispatch(meActions.setScreenShareInProgress(true));

		this._screenSharingProducer.close();

		store.dispatch(producerActions.removeProducer(this._screenSharingProducer.id));

		try {
			await this.sendRequest('closeProducer', { producerId: this._screenSharingProducer.id });
		}
		catch (error) {
			logger.error('disableScreenSharing() [error:"%o"]', error);
		}

		this._screenSharingProducer = null;

		store.dispatch(roomActions.setMyScreenSharing(false));
		this._screenSharing.stop();

		store.dispatch(meActions.setScreenShareInProgress(false));
	}

	async disableExtraVideo(id)
	{
		logger.debug('disableExtraVideo()');

		const producer = this._extraVideoProducers.get(id);

		if (!producer)
			return;

		store.dispatch(meActions.setWebcamInProgress(true));

		producer.close();

		store.dispatch(
			producerActions.removeProducer(id));

		try
		{
			await this.sendRequest(
				'closeProducer', { producerId: id });
		}
		catch (error)
		{
			logger.error('disableWebcam() [error:"%o"]', error);
		}

		this._extraVideoProducers.delete(id);

		store.dispatch(meActions.setWebcamInProgress(false));
	}

	async disableWebcam(source)
	{
		logger.debug('disableWebcam() | source:', source);

		if (!this._webcamProducer)
			return;

		store.dispatch(meActions.setWebcamInProgress(true));

		const { virtualRealityEnabled, virtualBgUrl } = store.getState().me;
		if (virtualRealityEnabled && virtualBgUrl) {

			this.virtualReality.stopStream();
			//this.virtualReality.stopStreamEffect();
		}

		this._webcamProducer.close();

		store.dispatch(producerActions.removeProducer(this._webcamProducer.id));

		try
		{
			await this.sendRequest('closeProducer', { 
				producerId: this._webcamProducer.id 
			});
		}
		catch (error)
		{
			logger.error('disableWebcam() [error:"%o"]', error);
		}

		this._webcamProducer = null;
		store.dispatch(settingsActions.setVideoMuted(true));

		// bingofree 更新spotlights中peer的videoMuted属性值
		await this._spotlights.updatePeerVideoMute(this.peerId, true, 'disableWebcam')
		store.dispatch(meActions.setWebcamInProgress(false));
	}

	async _setNoiseThreshold(threshold)
	{
		logger.debug('_setNoiseThreshold() [threshold:"%s"]', threshold);

		this._hark.setThreshold(threshold);

		store.dispatch(
			settingsActions.setNoiseThreshold(threshold));
	}

	async _updateAudioDevices()
	{
		logger.debug('_updateAudioDevices()');

		// Reset the list.
		this._audioDevices = {};

		try
		{
			logger.debug('_updateAudioDevices() | calling enumerateDevices()');

			const devices = await navigator.mediaDevices.enumerateDevices();
			const { selectedAudioDevice } = store.getState().settings;

			let defaultDeviceId = '';
			for (const device of devices)
			{
				if (device.kind !== 'audioinput')
					continue;

				this._audioDevices[device.deviceId] = device;

				if (!defaultDeviceId) {
					defaultDeviceId = device.deviceId;
				}
			}

			// 更新列表
			store.dispatch(meActions.setAudioDevices(this._audioDevices));

			if (!selectedAudioDevice || !this._audioDevices[selectedAudioDevice]) {
				// 更新选项
				defaultDeviceId && store.dispatch(settingsActions.setSelectedAudioDevice(defaultDeviceId))
			}
		}
		catch (error)
		{
			logger.error('_updateAudioDevices() [error:"%o"]', error);
		}
	}

	async _updateAudioOutputDevices()
	{
		logger.debug('_updateAudioOutputDevices()');

		// Reset the list.
		this._audioOutputDevices = {};

		try
		{
			logger.debug('_updateAudioOutputDevices() | calling enumerateDevices()');

			const devices = await navigator.mediaDevices.enumerateDevices();
			const { selectedAudioOutputDevice } = store.getState().settings;
			let defaultDeviceId = '';
			for (const device of devices)
			{
				if (device.kind !== 'audiooutput')
					continue;

				this._audioOutputDevices[device.deviceId] = device;
				if (!defaultDeviceId) {
					defaultDeviceId = device.deviceId;
				}
			}

			store.dispatch(meActions.setAudioOutputDevices(this._audioOutputDevices));
			if (!selectedAudioOutputDevice || !this._audioOutputDevices[selectedAudioOutputDevice]) {
				defaultDeviceId && store.dispatch(settingsActions.setSelectedAudioOutputDevice(defaultDeviceId))
			}
		}
		catch (error)
		{
			logger.error('_updateAudioOutputDevices() [error:"%o"]', error);
		}
	}

	async _updateWebcams()
	{
		logger.debug('_updateWebcams()');

		// Reset the list.
		this._webcams = {};

		try
		{
			logger.debug('_updateWebcams() | calling enumerateDevices()');

			const devices = await navigator.mediaDevices.enumerateDevices();
			const { selectedWebcam } = store.getState().settings;
			let defaultDeviceId = '';
			for (const device of devices)
			{
				if (device.kind !== 'videoinput')
					continue;

				this._webcams[device.deviceId] = device;
				if (!defaultDeviceId) {
					defaultDeviceId = device.deviceId;
				}
			}

			logger.debug('_updateWebcams() | calling enumerateDevices()', this._webcams);

			store.dispatch(meActions.setWebcamDevices(this._webcams));
			if (!selectedWebcam || !this._webcams[selectedWebcam]) {
				defaultDeviceId && store.dispatch(settingsActions.setSelectedWebcamDevice(defaultDeviceId))
			}
		}
		catch (error)
		{
			logger.error('_updateWebcams() [error:"%o"]', error);
		}
	}

	async _getAudioDeviceId()
	{
		logger.debug('_getAudioDeviceId()');

		try
		{
			logger.debug('_getAudioDeviceId() | calling _updateAudioDeviceId()');

			await this._updateAudioDevices();

			const { selectedAudioDevice } = store.getState().settings;

			if (selectedAudioDevice && this._audioDevices[selectedAudioDevice])
				return selectedAudioDevice;
			else
			{
				const audioDevices = Object.values(this._audioDevices);

				return audioDevices[0] ? audioDevices[0].deviceId : null;
			}
		}
		catch (error)
		{
			logger.error('_getAudioDeviceId() [error:"%o"]', error);
		}
	}

	async _getWebcamDeviceId()
	{
		logger.debug('_getWebcamDeviceId()');

		try
		{
			logger.debug('_getWebcamDeviceId() | calling _updateWebcams()');

			await this._updateWebcams();

			const { selectedWebcam } = store.getState().settings;

			if (selectedWebcam && this._webcams[selectedWebcam])
				return selectedWebcam;
			else
			{
				const webcams = Object.values(this._webcams);

				return webcams[0] ? webcams[0].deviceId : null;
			}
		}
		catch (error)
		{
			logger.error('_getWebcamDeviceId() [error:"%o"]', error);
		}
	}
}
